/* Social, on the Momentum screen pattern — phase A1 of
 * docs/social-redesign-spec.md (designed with her 2026-09-24).
 *
 * The old screen assumed Brill writes first: a form fed Generate, the text box
 * appeared only once Brill had produced something, and "I'll write it myself"
 * had no door. Here the post is a BOX. She can type in it, or open Chat beside
 * it and have Brill draft into it, or both. The shape every other panel has:
 * a table of posts (the top level — Social has no Level 1, so like Offers and
 * Outreach it keeps a Table / Post toggle, rule 9b), and a record screen with
 * the rail of posts beside the one she is writing.
 *
 * NOTHING TO PRESS (rule 1): a post saves as she leaves a field, from the moment
 * it has any words. Brill does not post anywhere (spec) — what leaves Brill is
 * words she copies, so the post and the first comment each carry their own Copy.
 *
 * A2 (2026-09-25): the BRILL BUTTON under the box — its menu by box state
 * (`brillMenu`), one-taps that land in a card under the box and never in her
 * words until she says so (rule 4, spec decision 6) — and the PICTURE button:
 * her GHL library where it is connected, a pasted link everywhere.
 *
 * STILL NOT HERE (A3): "Draft it for me" as its own pop-up with the picture
 * section and "Start from an idea". Until then "Draft it for me…" opens Chat,
 * which with the box empty DRAFTS (the old "pick a pillar and hook for me") and
 * with words in the box EDITS them (the old refine).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { ArrowLeft, ArrowUp, CalendarDays, Check, ChevronDown, ChevronLeft, ChevronRight, Copy, Facebook, Layers, Linkedin, List, Undo2, Lightbulb, Eye, PenLine, Send, Settings, Sparkles, Image as ImageIcon, Trash2, X } from "lucide-react";
import { BRAND } from "../../constants/colors";
import { SOCIAL_HOOK_CATEGORIES } from "../../constants/socialHookCategories";
import { parsePostBlock, stripPostBlock } from "../../utils/postBlock";
import { brillTurn, isQuestion, lastRewrite, noteTurn, sendHistory, useSocialChat, userTurn, versionsOf } from "../../utils/socialChat";
import { DRAFT_IS_LAST, OWN_WORDS_KINDS, adaptRequest, cleanAdapted, familyOf, fromLibraryPatch, pickedPicture, postRequest, saveAsLengths, savedAsPost, writtenText } from "../../utils/socialDraft";
import { parsePillars } from "../../utils/pillars";
import { ctaBrief, ctaStatus } from "../../utils/useCtas";
import { LIBRARY_KINDS, aboutFromLibrary, addUse, isUnused, libraryView, metaFor, needsPermission, usedByStamp, usedLabel, useLibrary } from "../../utils/library";
import { POST_LENGTHS, SOCIAL_PLATFORMS, askOf, brillMenu, defaultLength, pictureOf, platformOf, monthGrid, postDay, postHasWords, postState, postWords, postsByDay, previewFold, sendMoment, postsView, postPillars, quoteOf, tapAsk, withPicture, withQuote, withoutPicture, wordDiff } from "../../utils/socialPosts";
import { B, btnGhost, input, nb, pDesc, pTitle, quickBtn } from "./tokens";
import { AddButton, Bread, Choice, Field, IconBtn, Menu2, PageTitleField, SavedTick, StateDots, Status, floatingPanel, useAnchoredPanel, useTwoPane } from "./ui";
import { addDays, arcRows, arcStates, dayLabel, today as todayIso } from "../../utils/socialArcs";
import { BarBack, L1Table } from "./shell";
import { useBrowseNarrow } from "../BrowseTable";
import { MediaPickButton, useCrmConnected } from "./mediaPicker";

/* What the panel is — visible on the landing screen, behind the crumb's (i) on
   a post (CLAUDE.md 9e). Only what she cannot see by looking. */
export const SOCIAL_HINT = <>Your posts, written here and copied out to post. Brill never posts for you: each post and its <b style={nb}>first comment</b> has its own Copy. Write it yourself, or press <b style={nb}>Brill</b> under the box to have it drafted or tightened from your pillars, your Voice and your locked foundation.</>;

export const SOCIAL_COLS = "2.5fr .65fr .85fr 1fr .5fr .6fr 34px";

const firstLine = (s) => String(s || "").split("\n").map((l) => l.trim()).find(Boolean) || "";


/* GHL accounts, read once a visit; and the last "Post as" per platform —
   a per-browser convenience (localStorage), so every read is guarded. */
let ghlAccountsCache = null;
const postAsKey = (platform) => `brill.social.postAs.${platform}`;
export const readPostAs = (platform) => { try { return window.localStorage.getItem(postAsKey(platform)) || ""; } catch { return ""; } };
/* The time she sends at, remembered per browser — "9am" is hers to set once. */
const SEND_TIME_KEY = "brill.social.sendTime";
export const readSendTime = () => { try { return window.localStorage.getItem(SEND_TIME_KEY) || "09:00"; } catch { return "09:00"; } };
export const writeSendTime = (t) => { try { window.localStorage.setItem(SEND_TIME_KEY, t); } catch { /* private window */ } };
export const writePostAs = (platform, id) => { try { window.localStorage.setItem(postAsKey(platform), id); } catch { /* private window */ } };


/* ── The table: every post ───────────────────────────────────────────────────
   HER CALLS, 2026-10-06, looking at the table: "arcs and posts should probably
   be toggles … it could just get kind of long"; "all the posts are in the
   posts but you can access the arc by clicking on the arc link or toggling to
   the arc list"; platform ICONS, "the standard colors"; and "post date would
   be more important" than when it was last edited, with a DOT for status.

   So: Posts | Arcs where Table | Post used to sit (that toggle went to the
   same place as + Add and the newest post). POSTS holds every post — an arc's
   too, named with a link to its arc — soonest first by the day it is for.
   The dot is pressable, like the rail's (rule 9f): it marks Done without
   opening the post, and stops the click so the row does not open as well. */
const SOCIAL_TAB_KEY = "brill.social.tab";
const readTab = () => { try { const t = window.localStorage.getItem(SOCIAL_TAB_KEY); return t === "arcs" || t === "calendar" ? t : "posts"; } catch { return "posts"; } };
const writeTab = (t) => { try { window.localStorage.setItem(SOCIAL_TAB_KEY, t); } catch { /* private window */ } };

export function SocialTable({ posts = [], arcs = [], loading, error, onDismissError, onOpen, onDelete, onAdd, onAddArc, onOpenArc, onDeleteArc, onMark }) {
  const [adding, setAdding] = useState(false);
  const [tab, setTabState] = useState(readTab);
  const setTab = (t) => { setTabState(t); writeTab(t); };
  const arcOf = (p) => (p.arcId ? arcs.find((a) => a.id === p.arcId) || null : null);
  /* FILTERS AND SORT on Posts (her ask, 2026-10-09): platform, pillar and
     status above the table, any heading sorts (again to reverse), and the
     post she last worked on first by default — postsView. Not remembered
     between visits: a filter left on from last week reads as posts gone. */
  const [filt, setFilt] = useState({ platform: "all", pillar: "all", status: "all" });
  const [sort, setSort] = useState({ key: "recent", dir: "desc" });
  const narrowTable = useBrowseNarrow();
  const pillarsUsed = useMemo(() => postPillars(posts), [posts]);
  const filtered = filt.platform !== "all" || filt.pillar !== "all" || filt.status !== "all";
  const rows = tab === "arcs" ? arcs.map((a) => ({ ...a, _arc: true })) : postsView(posts, { ...filt, sort });
  const sortBy = (key) => setSort((cur) => (cur.key === key
    ? { key, dir: cur.dir === "asc" ? "desc" : "asc" }
    : { key, dir: key === "title" || key === "platform" || key === "pillar" || key === "date" ? "asc" : "desc" }));
  const heading = (key, label) => (
    <button type="button" onClick={() => sortBy(key)} aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      title={key === "date" ? "Coming up first, then the days gone. Again for the latest day first." : `Sort by ${label.toLowerCase()}`}
      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", letterSpacing: "inherit", textTransform: "inherit", fontWeight: 700,
        color: sort.key === key ? B.teal : "inherit" }}>
      {label}{sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
    </button>
  );
  const sel = { ...input, width: "auto", padding: "7px 10px", fontSize: 12.5 };
  const dateCell = (iso) => <div style={{ fontSize: 12, color: iso ? B.mid : B.ghost, whiteSpace: "nowrap" }}>{iso ? dayLabel(iso) : "—"}</div>;

  const postCells = (p, narrow = false) => {
    const st = postState(p);
    const arc = arcOf(p);
    const sub = arc ? (
      <button type="button" onClick={(e) => { e.stopPropagation(); onOpenArc(arc); }}
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 11.5, fontWeight: 600, color: B.teal, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "left" }}>
        Arc · {arc.name || "Untitled arc"}
      </button>
    ) : <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{firstLine(p.caption) || "Nothing written yet"}</span>;
    const name = (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: narrow ? 14.5 : 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title || "Untitled post"}</div>
        <div style={{ fontSize: 11.5, color: B.dim, marginTop: 2, overflow: "hidden", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
          {narrow && <PlatformIcon id={p.platform} size={12} />}
          {narrow && postDay(p) && <span style={{ flexShrink: 0 }}>{dayLabel(postDay(p))} ·</span>}
          {sub}
        </div>
      </div>
    );
    const dot = st === "empty"
      ? <span title="Not written yet" style={{ width: 9, height: 9, borderRadius: "50%", background: B.ghost, display: "inline-block" }} />
      : <MarkDot done={st === "done"} onMark={(next) => onMark?.(p, next)} />;
    if (narrow) return <>{name}<div style={{ display: "flex", alignItems: "center", gap: 6 }}>{dot}<ChevronRight size={16} color={B.faint} /></div></>;
    return (
      <>
        {name}
        <div><PlatformIcon id={p.platform} /></div>
        {dateCell(postDay(p))}
        <div style={{ fontSize: 12, color: p.inputs?.pillar ? B.mid : B.ghost, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.inputs?.pillar || "—"}</div>
        <div style={{ fontSize: 12, color: B.mid }}>{postWords(p) || "—"}</div>
        <div>{dot}</div>
      </>
    );
  };

  /* THE ARC'S ROW: its name and days, a dot per post (StateDots), its start,
     the words across every post, and "k of n" — counted, never stored. */
  const arcCells = (a, narrow = false) => {
    const { written, total } = arcRows(a, posts);
    const mine = posts.filter((x) => x.arcId === a.id);
    const words = mine.reduce((n, x) => n + postWords(x), 0);
    const allDone = total > 0 && arcStates(a, posts).every((s) => s === "done");
    const name = (
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: narrow ? 14.5 : 13.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name || "Untitled arc"}</div>
        <div style={{ fontSize: 11.5, color: B.dim, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}>
          {narrow && <PlatformIcon id={a.platform} size={12} />}
          {total ? `${a.brief?.days || "?"} days from ${dayLabel(a.startDate)}` : "Not planned yet"}
        </div>
      </div>
    );
    const status = <Status cls={allDone ? "locked" : "draft"} text={allDone ? "Done" : `${written} of ${total || "?"}`} />;
    if (narrow) return <>{name}<div style={{ display: "flex", alignItems: "center", gap: 6 }}>{status}<ChevronRight size={16} color={B.faint} /></div></>;
    return (
      <>
        {name}
        <div><PlatformIcon id={a.platform} /></div>
        {dateCell(a.startDate)}
        <div style={{ minWidth: 0 }}>{total ? <StateDots states={arcStates(a, posts)} noun="post" /> : <span style={{ fontSize: 12, color: B.ghost }}>—</span>}</div>
        <div style={{ fontSize: 12, color: B.mid }}>{words || "—"}</div>
        <div>{status}</div>
      </>
    );
  };

  return (
    <>
      <Bread text="Grow" />
      <h2 style={pTitle}>Social</h2>
      <p style={pDesc}>{SOCIAL_HINT}</p>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
        <div style={{ position: "relative", display: "inline-block" }}>
          <AddButton label="Add" onClick={() => setAdding((v) => !v)} />
          {adding && (
            <Menu2 head="What are you writing?" items={[
              ["✍️", "A single post", "One post. Write it yourself, or have Brill draft it.", false, () => { setAdding(false); onAdd(); }],
              ["🧵", "A multi-post arc", "A series over 3, 7 or 14 days. Brill plans it, then drafts each post from the ones you've finished.", false, () => { setAdding(false); onAddArc(); }],
            ]} />
          )}
        </div>
        <Seg value={tab} onChange={setTab} options={[
          { id: "posts", label: `Posts${posts.length ? ` · ${posts.length}` : ""}`, icon: List },
          { id: "arcs", label: `Arcs${arcs.length ? ` · ${arcs.length}` : ""}`, icon: Layers },
          { id: "calendar", label: "Calendar", icon: CalendarDays },
        ]} />
      </div>
      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 16, fontSize: 13, color: "var(--danger-text)", cursor: "pointer" }}>
          {error}
        </div>
      )}
      {tab === "posts" && posts.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <select value={filt.platform} onChange={(e) => setFilt({ ...filt, platform: e.target.value })} aria-label="Filter by platform" style={sel}>
            <option value="all">All platforms</option>
            {SOCIAL_PLATFORMS.map((pl) => <option key={pl.id} value={pl.id}>{pl.label}</option>)}
          </select>
          <select value={filt.pillar} onChange={(e) => setFilt({ ...filt, pillar: e.target.value })} aria-label="Filter by pillar" style={{ ...sel, maxWidth: 220 }}>
            <option value="all">All pillars</option>
            {pillarsUsed.map((pl) => <option key={pl} value={pl}>{pl}</option>)}
            <option value="">No pillar</option>
          </select>
          <select value={filt.status} onChange={(e) => setFilt({ ...filt, status: e.target.value })} aria-label="Filter by status" style={sel}>
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="done">Done</option>
            <option value="empty">Not written</option>
          </select>
          {/* A phone has no headings to press, so the sort is a choice here. */}
          {narrowTable && (
            <select value={`${sort.key}:${sort.dir}`} onChange={(e) => { const [key, dir] = e.target.value.split(":"); setSort({ key, dir }); }} aria-label="Sort posts" style={sel}>
              <option value="recent:desc">Last worked on</option>
              <option value="date:asc">Coming up</option>
              <option value="date:desc">Latest date</option>
              <option value="status:asc">Status</option>
            </select>
          )}
          {filtered && (
            <>
              <span style={{ fontSize: 12, color: B.dim }}>{rows.length} of {posts.length}</span>
              <button type="button" onClick={() => setFilt({ platform: "all", pillar: "all", status: "all" })}
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: B.teal }}>Clear</button>
            </>
          )}
        </div>
      )}
      {tab === "calendar" ? <CalendarView posts={posts} onOpen={onOpen} /> : (
      <L1Table
        records={rows} cols={SOCIAL_COLS}
        labels={tab === "arcs" ? ["Arc", "Platform", "Starts", "Posts", "Words", "Status", ""]
          : [heading("title", "Post"), heading("platform", "Platform"), heading("date", "Date"), heading("pillar", "Pillar"), heading("words", "Words"), heading("status", "Status"), ""]}
        loading={loading} loadingText={tab === "arcs" ? "Loading your arcs…" : "Loading your posts…"}
        empty={tab === "arcs" ? "No arcs yet. Add a multi-post arc to plan a series." : filtered ? "No posts match. Clear the filters to see them all." : "No posts yet. Add a single post or a multi-post arc."}
        onOpen={(r) => (r._arc ? onOpenArc(r) : onOpen(r))}
        onDelete={(r) => (r._arc ? onDeleteArc(r) : onDelete(r))} deleteTitle="Delete"
        rowFor={(r) => (r._arc ? arcCells(r) : postCells(r))}
        narrowRowFor={(r) => (r._arc ? arcCells(r, true) : postCells(r, true))}
      />
      )}
    </>
  );
}

/* ── The Calendar tab (her read, 2026-10-06: Content Plan is "just a calendar
   view of your posts. I don't think it needs much more than that") ──────────
   Every post on the day it is for — an arc's too — with its platform and its
   dot; tapping one opens it. A month grid where there is room, and on a phone
   the same month as a list of days, because seven columns of 50px cannot
   hold a post's name. Posts with no day sit under the month, so none is
   missing from it. */
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
/* DONE ONLY (her ask, 2026-10-06): the month as it is going out. Done is
   Done however it got there — her mark, or a send to GHL — so it reads the
   same with or without GHL. Remembered per browser. */
const DONE_ONLY_KEY = "brill.social.calendarDoneOnly";
const readDoneOnly = () => { try { return window.localStorage.getItem(DONE_ONLY_KEY) === "1"; } catch { return false; } };
const writeDoneOnly = (v) => { try { window.localStorage.setItem(DONE_ONLY_KEY, v ? "1" : "0"); } catch { /* private window */ } };

function CalendarView({ posts = [], onOpen }) {
  const wide = useTwoPane(760);
  const now = new Date();
  const [ym, setYm] = useState([now.getFullYear(), now.getMonth()]);
  const [doneOnly, setDoneOnlyState] = useState(readDoneOnly);
  const setDoneOnly = (v) => { setDoneOnlyState(v); writeDoneOnly(v); };
  const [y, m] = ym;
  const byDay = postsByDay(doneOnly ? posts.filter((p) => postState(p) === "done") : posts);
  const today = todayIso();
  const monthKey = `${y}-${String(m + 1).padStart(2, "0")}`;
  const weeks = monthGrid(y, m);
  const inMonth = (iso) => iso.slice(0, 7) === monthKey;
  const monthName = new Date(y, m, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const step = (n) => setYm(([yy, mm]) => { const d = new Date(yy, mm + n, 1); return [d.getFullYear(), d.getMonth()]; });
  const undated = byDay[""] || [];
  const monthDays = Object.keys(byDay).filter((d) => d && inMonth(d)).sort();

  const chip = (p) => {
    const st = postState(p);
    return (
      <button key={p.id} type="button" onClick={() => onOpen(p)} title={p.title || "Untitled post"}
        style={{ display: "flex", alignItems: "center", gap: 5, width: "100%", minWidth: 0, border: `1px solid ${B.border}`, background: B.surface, borderRadius: 7, padding: "3px 6px", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
        <PlatformIcon id={p.platform} size={12} />
        <span style={{ flex: 1, minWidth: 0, fontSize: 11.5, color: B.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.title || "Untitled post"}</span>
        <span aria-label={st === "done" ? "Done" : st === "empty" ? "Not written" : "Draft"} style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0, background: st === "done" ? B.teal : st === "empty" ? B.ghost : B.amberFill }} />
      </button>
    );
  };
  const nav = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
      <IconBtn icon={ChevronLeft} label="Previous month" onClick={() => step(-1)} size={16} />
      <b style={{ fontSize: 15, color: B.text, minWidth: 150, textAlign: "center" }}>{monthName}</b>
      <IconBtn icon={ChevronRight} label="Next month" onClick={() => step(1)} size={16} />
      {(y !== now.getFullYear() || m !== now.getMonth()) && (
        <button type="button" onClick={() => setYm([now.getFullYear(), now.getMonth()])} style={{ ...btnGhost, padding: "5px 10px", fontSize: 12, marginLeft: 4 }}>Today</button>
      )}
      <span style={{ flex: 1 }} />
      <button type="button" role="switch" aria-checked={doneOnly} onClick={() => setDoneOnly(!doneOnly)}
        style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: doneOnly ? B.teal : B.dim, padding: 4 }}>
        <span aria-hidden="true" style={{ width: 30, height: 17, borderRadius: 9, background: doneOnly ? "var(--accent)" : B.ghost, position: "relative", transition: "background 120ms ease" }}>
          <span style={{ position: "absolute", top: 2, left: doneOnly ? 15 : 2, width: 13, height: 13, borderRadius: "50%", background: "#fff", transition: "left 120ms ease" }} />
        </span>
        Done only
      </button>
    </div>
  );

  return (
    <div>
      {nav}
      {wide ? (
        <div style={{ border: `1px solid ${B.border}`, borderRadius: 12, overflow: "hidden", background: B.surface }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", background: B.surface2, borderBottom: `1px solid ${B.border}` }}>
            {WEEKDAYS.map((d) => <div key={d} style={{ padding: "7px 8px", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, color: B.label }}>{d}</div>)}
          </div>
          {weeks.map((week, wi) => (
            <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", borderBottom: wi === weeks.length - 1 ? "none" : `1px solid ${B.border}` }}>
              {week.map((iso, di) => (
                <div key={iso} style={{ minHeight: 92, padding: 6, display: "flex", flexDirection: "column", gap: 4, minWidth: 0,
                  borderRight: di === 6 ? "none" : `1px solid ${B.border}`, background: inMonth(iso) ? B.surface : B.surface2 }}>
                  <span style={{ fontSize: 11.5, fontWeight: iso === today ? 700 : 500, color: iso === today ? "var(--btn-text)" : inMonth(iso) ? B.mid : B.faint,
                    alignSelf: "flex-start", ...(iso === today ? { background: "var(--accent)", borderRadius: 10, padding: "0 6px" } : {}) }}>
                    {Number(iso.slice(8))}
                  </span>
                  {(byDay[iso] || []).map(chip)}
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {monthDays.map((iso) => (
            <div key={iso}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: iso === today ? B.teal : B.label, marginBottom: 5 }}>{dayLabel(iso)}{iso === today ? " · Today" : ""}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>{byDay[iso].map(chip)}</div>
            </div>
          ))}
          {!monthDays.length && <div style={{ fontSize: 13, color: B.dim, padding: "8px 0" }}>{doneOnly ? `Nothing done on a day in ${monthName.split(" ")[0]} yet.` : `Nothing on a day in ${monthName.split(" ")[0]} yet.`}</div>}
        </div>
      )}
      {undated.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: B.label, marginBottom: 6 }}>No day yet · {undated.length} <span style={{ fontWeight: 400, color: B.faint }}>— give one a day on its screen, or when you send it to GHL</span></div>
          <div style={{ display: "grid", gridTemplateColumns: wide ? "repeat(3, minmax(0, 1fr))" : "1fr", gap: 5 }}>{undated.map(chip)}</div>
        </div>
      )}
    </div>
  );
}

/* THE DOT, PRESSABLE (rule 9f, as in the rail): toggles Done, says "Done" and
   fades when she finishes one, says nothing when she takes it back, and stops
   the click so marking a post does not open it. */
function MarkDot({ done, onMark }) {
  const [flash, setFlash] = useState(0);
  useEffect(() => {
    if (!flash) return undefined;
    const t = setTimeout(() => setFlash(0), 1300);
    return () => clearTimeout(t);
  }, [flash]);
  const label = done ? "Done — mark it as still a draft" : "Draft — mark it done";
  return (
    <button type="button" title={label} aria-label={label}
      onClick={(e) => { e.stopPropagation(); onMark(done ? "draft" : "done"); if (!done) setFlash(Date.now()); }}
      style={{ border: "none", background: "none", padding: "8px 6px", margin: "-8px -6px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 10, height: 10, borderRadius: "50%", background: done ? B.teal : B.amberFill, display: "inline-block" }} />
      {flash > 0 && <span style={{ fontSize: 10.5, fontWeight: 700, color: B.teal }}>Done</span>}
    </button>
  );
}

/* THE PLATFORMS' MARKS (her calls, 2026-10-06). First "the standard colors,
   not a teal Instagram"; then, seeing the brand blues beside her palette,
   "the IN and F should be teal, Instagram could be multi color still" — and
   "can the IN be dark teal and F be light teal". So LinkedIn wears the deep
   teal and Facebook the bright one (the theme's own pair, --accent-deep and
   --accent, so it holds in light and dark), and Instagram keeps its own
   gradient — standing out is "kind of the point". The name rides as the accessible label and the tooltip, so
   nothing is lost to someone who cannot see it. */
const PLATFORM_ICON = { linkedin: [Linkedin, "var(--accent-deep)"], facebook: [Facebook, "var(--accent)"] };
export function PlatformIcon({ id, size = 16 }) {
  const label = platformOf(id).label;
  const gid = `ig-${React.useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const [Icon, color] = PLATFORM_ICON[id] || [];
  return (
    <span role="img" aria-label={label} title={label} style={{ display: "inline-flex", color: color || B.teal, verticalAlign: "middle" }}>
      {Icon ? <Icon size={size} /> : (
        /* Lucide's Instagram mark, stroked with Instagram's own gradient. */
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <defs>
            <linearGradient id={gid} x1="2" y1="22" x2="22" y2="2" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#FEDA75" />
              <stop offset="0.3" stopColor="#FA7E1E" />
              <stop offset="0.55" stopColor="#D62976" />
              <stop offset="0.8" stopColor="#962FBF" />
              <stop offset="1" stopColor="#4F5BD5" />
            </linearGradient>
          </defs>
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke={`url(#${gid})`} />
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke={`url(#${gid})`} />
          <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke={`url(#${gid})`} />
        </svg>
      )}
    </span>
  );
}


/* ── The record screen: the post, and NO RAIL ───────────────────────────────
   Her call, 2026-09-25: "because it's only two levels this sidebar really
   needs to not be there on the social draft … you might add a new post but
   aren't skipping to old ones." This DELIBERATELY breaks the screen pattern's
   rail-beside-the-record for this one panel. The rail on a two-level panel is
   the Table drawn again, and on Social nobody moves sideways between posts —
   so the post gets the width, and the way to another one is `← All posts`.
   What the rail carried that nothing else did was the DONE MARK (rule 9f): it
   moved beside the post's name, as on a website page.

   THE HEADING ROWS LIVE IN PostEditor since 2026-10-06 (her read: "it's a lot
   of space before you get to the post"), because the phone puts the cog and
   the bin beside the title and those are the editor's. The Table / Post toggle
   went the same day: with no rail, Table and `← All posts` went to the same
   place (rule 9b — a toggle that changes level is a way back in disguise). */
export function SocialCopy({ children }) {
  return (
    <>
      <Bread text="Grow" crumb="Social" hint={SOCIAL_HINT} />
      {children}
    </>
  );
}


/* THE BOX FILLS THE SCREEN, its toolbar landing just above the bottom (her
   read, 2026-10-06: "it's still not as much as I would like"). Each number is
   everything ABOVE the box plus everything BELOW it (toolbar; on a phone the
   app's bottom bar too), measured on screen at 1440 and 375. */
const BOX_RESERVE_WIDE = 310;
const BOX_RESERVE_PHONE = 360;
const PICTURE_LINE = 60;
const BRILL_LINE = 58;
const REPLY_LINE = 66;

/* ── The post ─────────────────────────────────────────────────────────────── */
export function PostEditor({
  draft, setDraft, onSave, onDelete, saving, error, onDismissError,
  posts = [], offers = [], ctas = [], socialContext = {}, userSettings = {},
  /* The series this post belongs to (`arcContext`), or null. Rides on every
     draft this screen asks for, so a post in an arc is always written as one. */
  arc = null,
  /* The heading, drawn here rather than by SocialCopy (see there): the way
     back `{ label, onClick }`, the name field's `{ value, onChange, onCommit }`,
     Draft / Done, and the arc's "Post 2 of 4" line. */
  back = null, titleField = null, status = null, note = null,
  /* SAVE AS ANOTHER PLATFORM (2026-10-06): a new post id, the store's write
     (returns the saved row, or null), and the way to open another post. */
  newPostId = null, onCreatePost = null, onOpenPost = null,
}) {
  const { session } = useSession();
  const twoPane = useTwoPane(1080);
  const pillars = useMemo(() => parsePillars(socialContext.contentPillars), [socialContext.contentPillars]);
  /* THE CHAT LIVES UNDER THE POST (her pick from the mockup, 2026-10-06:
     "the chat screen feels like a separate area other than your post"). So
     beside the composer there is only the Preview, always; on a phone, the
     Post or the Preview. */
  const [phonePane, setPhonePane] = useState("post");
  const [cog, setCog] = useState(false);
  const [copied, setCopied] = useState(null);
  /* The conversation, KEPT on the post (supabase/0035, utils/socialChat.js).
     Persisted only once the post itself exists — a chat row points at it. */
  const chat = useSocialChat(draft?.id, { ready: !!draft && !draft.isNew });
  const [chatText, setChatText] = useState("");
  const [convoOpen, setConvoOpen] = useState(false);
  /* Read inside the send, which outlives the render it started in. */
  const convoRef = useRef(false);
  convoRef.current = convoOpen;
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  /* { platform, busy } while Brill writes it, { platform, post } once saved,
     { platform, error } if either failed. Drives the pop-up. */
  const [saveAs, setSaveAs] = useState(null);
  /* A REWRITE LANDS LIT: the words Brill changed, marked, until she taps the
     post to carry on (or ten seconds pass) — the "watch it change" half of
     what makes Claude feel live. */
  const [lit, setLit] = useState(null);
  useEffect(() => {
    if (!lit) return undefined;
    const t = setTimeout(() => setLit(null), 10000);
    return () => clearTimeout(t);
  }, [lit]);
  const boxRef = useRef(null);
  const lineRef = useRef(null);
  const chatLineRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [visualBusy, setVisualBusy] = useState(false);
  /* The one-tap result, waiting under the box (spec decision 6). `base` is the
     post it was made FROM: she can keep typing while it waits, and a card made
     from older words has to say so before it replaces the newer ones. */
  const [card, setCard] = useState(null);
  const [picOpen, setPicOpen] = useState(false);
  /* The Draft pop-up: "draft" writes into an empty box, "redraft" into a card. */
  const [draftForm, setDraftForm] = useState(null);
  const [libraryPick, setLibraryPick] = useState(false);
  const [ghl, setGhl] = useState(null);
  const [commentOpen, setCommentOpen] = useState(false);
  /* Declared BEFORE the accounts effect below reads it — a const read above
     its line is a crash at render that the build compiles happily. */
  const crm = useCrmConnected();
  /* HER GHL ACCOUNTS, for "Post as" — only where GHL is connected, once a
     visit (module cache), because the post screen opens often and the list
     changes only when she connects something in GHL. */
  const [ghlAccounts, setGhlAccounts] = useState(ghlAccountsCache);
  useEffect(() => {
    if (!crm || ghlAccountsCache) return undefined;
    let gone = false;
    (async () => {
      try {
        const token = await session?.getToken();
        const res = await fetch("/api/ghl", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ job: "social", action: "accounts" }),
        });
        const r = await res.json().catch(() => ({}));
        if (r.ok && Array.isArray(r.accounts) && r.accounts.every((x) => x && x.id)) { ghlAccountsCache = r.accounts; if (!gone) setGhlAccounts(r.accounts); }
      } catch { /* no list, no choice — Send says why if it needs one */ }
    })();
    return () => { gone = true; };
  }, [crm]);
  /* HER WHOLE LIBRARY, for "From your library" in the Draft pop-up — every
     kind, not just ideas (her call, 2026-09-28). The shared load: opening a
     post no longer reads the Library again. */
  const { rows: libraryItems, save: persistItem } = useLibrary();

  /* THE WAY OUT SAVES WHAT SHE TYPED, not the value from the last render —
     the race found on Me → Voice the same day (a blur before the last
     keystroke rendered saved the text from before it). */
  const latest = useRef(draft);
  latest.current = draft;
  const edit = (patch) => { const next = { ...latest.current, ...patch }; latest.current = next; setDraft(next); };
  const commit = () => { if (postHasWords(latest.current)) onSave(latest.current); };
  /* A choice has no "way out" — it saves on the press, as a patch. */
  const setNow = (patch) => { edit(patch); if (postHasWords(latest.current)) onSave(latest.current); };

  if (!draft) return null;
  const platform = platformOf(draft.platform);
  const caption = draft.caption || "";
  const empty = !caption.trim();
  const over = caption.length > platform.limit;
  const canSave = postHasWords(draft);
  const pillar = pillars.find((p) => p.label === draft.inputs?.pillar) || null;
  const usableCtas = ctas.filter((c) => ctaStatus(c) !== "draft" || c.id === draft.ctaId);
  const chosenCta = ctas.find((c) => c.id === draft.ctaId) || null;

  const copy = (text, which) => {
    navigator.clipboard?.writeText(text);
    setCopied(which);
    setTimeout(() => setCopied((c) => (c === which ? null : c)), 2000);
  };

  /* ── Brill ─────────────────────────────────────────────────────────────── */
  const callSocial = async (body) => {
    const token = await session?.getToken();
    const res = await fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ mode: "social", moduleId: "social_generator", clientContext: socialContext.clientContext, ...body }),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e?.error || `Error ${res.status}`);
    }
    return res.json();
  };
  /* WITH THE BOX EMPTY, CHAT DRAFTS. The pillar and hook default to the quietest
     ones she has not used lately (suggestNextPost) — spec decision 4: the form
     asks one thing and defaults the rest. What she typed is the angle.
     `choice` is what she picked in the Draft pop-up (a pillar label and a hook
     id, either of which may be "" for "Let Brill pick"); without it the post's
     own settings decide, then Brill. The request itself is `postRequest`, the
     SAME one an arc's sparkle sends — and a post in an arc carries the arc
     (`arc`, from the hub), whichever path drafts it. */
  const writePost = async (angle, choice = null) => {
    const { body, patchFor } = postRequest({ draft: latest.current, choice, angle, pillars, offers, ctas, posts, arc });
    const written = writtenText(await callSocial(body));
    if (!written) return null;
    /* Read from `latest` at the moment it is APPLIED, which for a redraft is
       when she presses Use this. */
    return patchFor(written);
  };
  /* Words from a draft landing in the post. An idea that fed them is stamped
     USED BY THIS POST (Library: an idea is used many times, so the stamp is
     added, once per post) — the post already exists, so there is no
     provisional stamp to confirm later. */
  const landDraft = (patchFor) => {
    const { _idea, ...patch } = patchFor(latest.current);
    edit(patch);
    onSave(latest.current);
    if (_idea) {
      const item = libraryItems.find((i) => i.id === _idea.id) || _idea;
      if (!(item.usedBy || []).some((u) => u.type === "post" && u.id === latest.current.id)) {
        persistItem({ ...item, usedBy: addUse(item, usedByStamp("post", latest.current.id, latest.current.title || null)) });
      }
    }
  };
  /* WITH WORDS IN THE BOX, CHAT EDITS THEM: the post as it stands rides in
     every request (the [DRAFT] block), and only a rewritten block replaces it.
     `messages` is already shaped — sendHistory for the chat, one ask for a tap. */
  const askRefine = async (messages) => {
    const data = await callSocial({
      kind: "refine",
      post: latest.current.caption,
      platform: platform.id,
      pillar: pillar ? [pillar.label, pillar.subtitle].filter(Boolean).join(" — ") : null,
      theme: draft.inputs?.theme || "",
      hookLabel: SOCIAL_HOOK_CATEGORIES.find((c) => c.id === draft.inputs?.hookCategory)?.label || null,
      ctaBrief: ctaBrief(chosenCta),
      limit: platform.limit,
      messages,
    });
    return data.content?.find((b) => b.type === "text")?.text || "";
  };

  /* THE BRILL LINE UNDER THE POST. Her message is kept on the post; Brill's
     answer lands IN the post — lit, with Undo — not in a pane beside it. Chat
     writes straight in by her call (2026-10-06): iterating five times with a
     "Use this" press each round is friction; the one-taps keep their card.
     Every rewrite keeps the post from just before it, so Undo and "Go back to
     this" survive a refresh. */
  const send = async (text) => {
    const msg = String(text || "").trim();
    if (!msg || sending) return;
    const before = latest.current.caption || "";
    const beforeDraft = latest.current.captionDraft ?? null;
    const mine = userTurn(msg);
    const convo = [...chat.ref.current, mine];
    chat.append(mine);
    setChatText("");
    setSending(true);
    setLit(null);
    try {
      if (!before.trim()) {
        /* AN EMPTY BOX: what she typed is what it's about. */
        const angle = /^draft (it|one)( for me)?\.?$/i.test(msg) ? "" : msg;
        const patchFor = await writePost(angle);
        if (!patchFor) { chat.append(brillTurn("I couldn't write that one — try again, or tell me more about what it's for.")); return; }
        landDraft(patchFor);
        chat.append(brillTurn("It's in the box. Tell me what to change, or edit it yourself.", { before, after: latest.current.caption, beforeDraft }));
        return;
      }
      const reply = await askRefine(sendHistory(convo));
      const rewritten = parsePostBlock(reply);
      const note = stripPostBlock(reply);
      /* A QUESTION IS NOT AN EDIT (her call, 2026-10-06). If Brill rewrote
         anyway, the rewrite waits in a card, like a one-tap's — her post is
         untouched until she presses Use this. */
      if (rewritten && isQuestion(msg)) {
        chat.append(brillTurn(note || "Here's how I'd change it."));
        setCard({ label: "Brill's suggestion", base: before, text: rewritten, note: "", fromChat: { beforeDraft } });
        return;
      }
      if (rewritten) {
        /* Brill's words, so they are the draft her later edits are measured
           against (captionDraft — see socialDraft.js). */
        edit({ caption: rewritten, captionDraft: rewritten, inputs: { ...(latest.current.inputs || {}), draftIs: DRAFT_IS_LAST } });
        onSave(latest.current);
        setLit(wordDiff(before, rewritten));
        /* A CHANGE IS SEEN IN THE POST (her call, 2026-10-06: "once a change
           has been applied, it might make sense to go back … so you can see
           it with that little preview"). From the full-page chat, a rewrite
           closes it and she lands on her post, lit, with Undo; an answer
           keeps her in the chat. */
        if (convoRef.current) setConvoOpen(false);
      }
      chat.append(brillTurn(note || (rewritten ? "Done." : "Try that again for me?"), rewritten ? { before, after: rewritten, beforeDraft } : null));
    } catch (err) {
      chat.append(noteTurn(/429/.test(err?.message || "") ? "That's the rate limit — give it a moment. Your post is untouched." : (err?.message || "I couldn't reach Brill just then. Your post is exactly as you left it.")));
    } finally {
      setSending(false);
    }
  };

  /* UNDO: only while the post is exactly what the last rewrite left — once she
     has typed over it, putting the old one back would throw her typing away. */
  const undo = () => {
    const lr = lastRewrite(chat.ref.current, latest.current.caption || "");
    if (!lr?.canUndo) return;
    edit({ caption: lr.turn.before, captionDraft: lr.turn.beforeDraft ?? null });
    onSave(latest.current);
    setLit(null);
    chat.append(noteTurn("You undid that change."));
  };
  /* "Go back to this", from the conversation: a version is what one rewrite
     left. It counts as Brill's words again, so voice learning measures her
     edits from it. */
  const goBackTo = (v) => {
    edit({ caption: v.after, captionDraft: v.after, inputs: { ...(latest.current.inputs || {}), draftIs: DRAFT_IS_LAST } });
    onSave(latest.current);
    setLit(null);
    setConvoOpen(false);
    chat.append(noteTurn(v.n === 1 ? "You went back to where you started." : `You went back to version ${v.n}.`));
  };

  /* THE QUOTE CARD — KEPT, as a Brill menu item (her answer to spec question
     2, 2026-09-25). A brief, not a picture: it rides beside an attached picture
     rather than replacing it (`withQuote`), and draws under the box. */
  const makeQuoteCard = async () => {
    if (empty || visualBusy) return;
    setVisualBusy(true);
    setCard(null);
    try {
      const data = await callSocial({ kind: "visual", visualType: "quote", post: latest.current.caption });
      const raw = data.content?.find((b) => b.type === "text")?.text || "";
      const brief = JSON.parse(raw.replace(/```json|```/g, "").trim());
      edit({ visual: withQuote(latest.current.visual, brief) });
      onSave(latest.current);
    } catch {
      setCard({ label: "Make a quote card", note: "I couldn't make the quote card just then. Try again in a moment.", text: null, base: latest.current.caption });
    } finally {
      setVisualBusy(false);
    }
  };

  /* ── The Brill button's one-taps ─────────────────────────────────────────
     Each rides the refine call with a fixed ask (`tapAsk`) and NO chat
     history: a tap is its own question, and the chat's turns are about a
     different moment of the post. The answer waits in a card — nothing
     reaches her words until she presses Use this. */
  const picture = pictureOf(draft?.visual);
  /* POST AS (her call: pick per post, and remember it). The post's own pick,
     else the last one she used on this platform, else the first. */
  const platformAccounts = (ghlAccounts || []).filter((a) => a.platform === (draft?.platform || "linkedin"));
  const remembered = readPostAs(draft?.platform || "linkedin");
  const postAs = [draft?.inputs?.ghlAccountId, remembered, platformAccounts[0]?.id].find((id) => id && platformAccounts.some((a) => a.id === id)) || "";
  const pickPostAs = (id) => {
    writePostAs(latest.current.platform || "linkedin", id);
    setNow({ inputs: { ...(latest.current.inputs || {}), ghlAccountId: id } });
  };
  const tapBusy = !!card?.busy;
  const runTap = async (item) => {
    if (sending || tapBusy || visualBusy) return;
    if (item.id === "ask") { lineRef.current?.focus(); return; }
    if (item.id === "draft" || item.id === "redraft") { setDraftForm(item.id); return; }
    if (item.id === "library") { setLibraryPick(true); return; }
    if (item.id === "quote") { makeQuoteCard(); return; }
    if (item.id === "picture" && !String(picture?.about || "").trim()) {
      setCard({ label: item.label, note: "Say what's in the picture first — the line beside it — so Brill knows what it is working in.", text: null, base: latest.current.caption });
      return;
    }
    const base = latest.current.caption;
    setCard({ label: item.label, busy: true, base });
    try {
      const ask = tapAsk(item.id, { platformLabel: platform.label, limit: platform.limit, about: picture?.about });
      const reply = await askRefine([{ role: "user", content: ask }]);
      const text = parsePostBlock(reply);
      setCard({ label: item.label, base, text, ask, reply, note: stripPostBlock(reply) || (text ? "" : "Brill didn't change anything. Try Chat and say what you're after.") });
    } catch (err) {
      setCard({ label: item.label, base, text: null, note: /429/.test(err?.message || "") ? "That's the rate limit — give it a moment. Your post is untouched." : "I couldn't reach Brill just then. Your post is exactly as you left it." });
    }
  };
  /* THE DRAFT POP-UP'S ANSWER (her ask, 2026-09-25: "open this so they can
     select their pillars … hook style … a separate dropdown"). A first draft
     goes straight into the empty box — there is nothing of hers to protect. A
     REDRAFT waits in a card like every other rewrite (rule 4): her post stays
     until she presses Use this. */
  const submitDraft = async ({ about, picture: picked, ...choice }) => {
    const mode = draftForm;
    setDraftForm(null);
    /* A picture picked in the pop-up goes on the post FIRST, so the draft is
       written with its "what's in it" line (postRequest reads the post). */
    if (picked?.url) edit({ visual: withPicture(latest.current.visual, { url: picked.url, about: picked.about, alt: picked.about }) });
    const base = latest.current.caption;
    const label = mode === "redraft" ? "Redraft from scratch" : "Draft it for me";
    setCard({ label, busy: true, base });
    try {
      const patchFor = await writePost(about, choice);
      if (!patchFor) { setCard({ label, base, text: null, note: "I couldn't write that one. Try again, or tell me more about what it's for." }); return; }
      if (mode === "draft" && !String(latest.current.caption || "").trim()) {
        landDraft(patchFor);
        setCard(null);
        return;
      }
      setCard({ label, base, text: patchFor(latest.current).caption, patchFor, note: "" });
    } catch (err) {
      setCard({ label, base, text: null, note: /429/.test(err?.message || "") ? "That's the rate limit — give it a moment. Your post is untouched." : "I couldn't reach Brill just then. Your post is exactly as you left it." });
    }
  };

  /* ── Send to GHL as a draft (her ask, 2026-09-25) ────────────────────────
     Saves first — the server reads her row, not this screen — then hands over
     the id. The GHL post id comes back into `inputs` here as well as being
     written there, or her next keystroke would save the old `inputs` over it
     and the next send would make a duplicate. */
  /* HER CALLS, 2026-10-06: "if it pushed to GHL it has a date, if it pushed
     to GHL it's also automatically DONE." So a send always carries the day
     and time from the send pop-up, the post keeps that day, and a send that
     GHL took marks the post Done. A deliberate exception to "Done is her
     mark": sending it IS her saying it is finished. */
  const sendToGhl = async ({ day, time, at }) => {
    if (empty || ghl?.busy) return;
    setSendOpen(false);
    setGhl({ busy: true });
    try {
      await onSave(latest.current);
      const token = await session?.getToken();
      const res = await fetch("/api/ghl", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ job: "social", postId: latest.current.id, accountId: postAs || null, scheduleAt: at }),
      });
      const r = await res.json().catch(() => ({}));
      if (!r.ok) { setGhl({ error: r.error || `GHL didn't take it (${res.status}).` }); return; }
      writeSendTime(time);
      edit({ scheduledDate: day, status: "done", inputs: { ...(latest.current.inputs || {}), ...(r.ghlPostId ? { ghlPostId: r.ghlPostId } : {}), ...(postAs ? { ghlAccountId: postAs } : {}) } });
      onSave(latest.current);
      const extras = [r.withPicture && "the picture", r.withComment && "the first comment"].filter(Boolean);
      setGhl({ ok: `${r.done === "updated" ? "Updated the draft" : "In your GHL Social Planner as a draft"} for ${(r.accounts || []).join(", ") || platform.label} on ${dayLabel(day)}${extras.length ? `, with ${extras.join(" and ")}` : ""} — marked Done. Schedule it there.` });
    } catch (err) {
      setGhl({ error: `Could not reach GHL — ${err.message}` });
    }
  };

  /* PICK FROM LIBRARY — her words, word for word, into the empty box. No
     Brill call. Stamped used by this post through landDraft, as a draft's
     idea is. Only ever offered on an empty box, so nothing of hers is lost. */
  const pickFromLibrary = (item) => {
    setLibraryPick(false);
    if (String(latest.current.caption || "").trim()) return;
    landDraft((cur) => fromLibraryPatch(cur, item));
  };

  const useCard = () => {
    if (!card?.text) return;
    if (card.patchFor) landDraft(card.patchFor);
    else {
      const before = latest.current.caption || "";
      const beforeDraft = latest.current.captionDraft ?? null;
      edit({ caption: card.text, captionDraft: card.text, inputs: { ...(latest.current.inputs || {}), draftIs: DRAFT_IS_LAST } });
      onSave(latest.current);
      /* A suggestion from the chat goes into the conversation as a version,
         so Undo and "Go back to this" cover it like any other rewrite. */
      if (card.fromChat) {
        chat.append(brillTurn("You used my suggestion — it's in your post.", { before, after: card.text, beforeDraft }));
        setLit(wordDiff(before, card.text));
        setConvoOpen(false);
      }
    }
    setCard(null);
  };
  /* A tap that answered instead of rewriting is the start of a conversation,
     so it moves INTO Chat as the first exchange rather than vanishing. */
  const cardToChat = () => {
    if (card?.ask && card?.reply) chat.append([userTurn(card.ask), brillTurn(stripPostBlock(card.reply) || "Done.")]);
    setCard(null);
    setPhonePane("post");
    lineRef.current?.focus();
  };

  /* ── Save as another platform ─────────────────────────────────────────────
     A PLAIN COPY saves at once, no Brill. ANOTHER PLATFORM opens a pop-up
     first (her ask, 2026-10-09): how long — One line / Short / Medium / Long —
     a hook (keep hers, or a style, drawn fresh each time so Regenerate
     rotates it), and a line of her own ("a little punchier"). Brill's version
     waits in the pop-up; nothing is created until she says Save it. Her post
     as it stands is saved first, so the new one links to a row that exists,
     and her post is never touched. */
  const saveAsPlatform = async (target) => {
    setSaveAsOpen(false);
    const src = latest.current;
    if (!postHasWords(src) || !onCreatePost || !newPostId || saveAs?.busy) return;
    if (target !== "copy") {
      setSaveAs({ stage: "setup", platform: target, length: defaultLength(target), hook: "keep", note: "" });
      return;
    }
    setSaveAs({ stage: "saved", platform: platform.id, plain: true, busy: true });
    try {
      await onSave(src);
      const post = savedAsPost(src, { id: newPostId(), platform: platform.id, caption: String(src.caption || ""), plain: true });
      const saved = await onCreatePost(post);
      if (!saved) { setSaveAs({ stage: "saved", platform: platform.id, plain: true, error: "It didn't save just then. Try again in a moment." }); return; }
      setSaveAs({ stage: "saved", platform: platform.id, plain: true, post: { ...post, ...saved, isNew: false } });
    } catch {
      setSaveAs({ stage: "saved", platform: platform.id, plain: true, error: "It didn't save just then. Your post is exactly as you left it." });
    }
  };
  /* Write (or Regenerate) Brill's version with what the pop-up holds now. */
  const writeSaveAs = async (s) => {
    if (!s || s.busy) return;
    const src = latest.current;
    setSaveAs({ ...s, busy: true, error: null });
    try {
      await onSave(src);
      const words = cleanAdapted(writtenText(await callSocial(adaptRequest(src, s.platform, { length: s.length, hook: s.hook, note: s.note }))));
      setSaveAs((cur) => (cur && cur.platform === s.platform
        ? { ...cur, busy: false, ...(words ? { stage: "preview", words } : { error: "Brill didn't write it just then. Try again in a moment." }) }
        : cur));
    } catch (err) {
      setSaveAs((cur) => (cur ? { ...cur, busy: false, error: /429/.test(err?.message || "") ? "That's the rate limit — give it a moment." : "I couldn't reach Brill just then. Your post is exactly as you left it." } : cur));
    }
  };
  /* Keep Brill's version as its own post. */
  const keepSaveAs = async (s) => {
    if (!s?.words || s.busy) return;
    setSaveAs({ ...s, busy: true, error: null });
    try {
      const post = savedAsPost(latest.current, { id: newPostId(), platform: s.platform, caption: s.words, length: s.length, hook: s.hook });
      const saved = await onCreatePost(post);
      if (!saved) { setSaveAs({ ...s, busy: false, error: "It was written but didn't save. Try again in a moment." }); return; }
      setSaveAs({ stage: "saved", platform: s.platform, post: { ...post, ...saved, isNew: false } });
    } catch {
      setSaveAs({ ...s, busy: false, error: "It was written but didn't save. Try again in a moment." });
    }
  };
  const family = familyOf(draft, posts);

  /* ── The picture ─────────────────────────────────────────────────────── */
  const attach = (url) => {
    const u = String(url || "").trim();
    if (!u) return;
    setNow({ visual: withPicture(latest.current.visual, { url: u }) });
    setPicOpen(false);
  };
  const detach = () => setNow({ visual: withoutPicture(latest.current.visual) });
  /* "What's in it" is most of an alt text already (spec), so it is both. */
  const setAbout = (about) => edit({ visual: { ...latest.current.visual, about, alt: about } });

  const lineHint = empty
    ? `Tell Brill what this ${platform.label} post is about, or say "draft it"…`
    /* "Reply…" once Brill has answered (her read, 2026-10-06: "What next?"
       is "not really right — Claude just says reply"). */
    : chat.turns.some((t) => t.role === "assistant") ? "Reply…" : "Tell Brill what to change…";
  const rewrite = lastRewrite(chat.turns, caption);
  const lastBrill = [...chat.turns].reverse().find((t) => t.role === "assistant" || t.role === "note") || null;

  /* ── The heading ───────────────────────────────────────────────────────────
     SAVED AND DELETE LIVE UP HERE, small (her call, 2026-09-25: "a bit useless
     down there … more compact at the top"). Rule 1's footer, moved rather than
     dropped — the read-out has to exist somewhere, because "Saved" and silence
     look identical. Delete still asks first (ConfirmDialog, in the hub). */
  const savedTick = <SavedTick saving={saving} problem={error ? "Didn't save — see the note below" : canSave ? "" : "Write something to save it"} />;
  const cogBtn = <IconBtn icon={Settings} label={cog ? "Hide post settings" : "Post settings"} onClick={() => { setCog((v) => !v); setPhonePane("post"); }} size={twoPane ? 15 : 17} />;
  const binBtn = !draft.isNew && <IconBtn icon={Trash2} label="Delete post" onClick={() => onDelete(draft)} size={twoPane ? 15 : 17} hover={B.salmon} />;
  const titleInput = titleField && (
    <PageTitleField value={titleField.value} onChange={titleField.onChange} onCommit={titleField.onCommit}
      placeholder="Name this post" label="Post name" compact={!twoPane} />
  );
  /* No Chat pane any more — the chat is under the post. Wide: the Preview is
     always beside it, so only a label. Phone: the post or its preview. */
  const paneSeg = twoPane
    ? <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, color: B.label }}><Eye size={13} /> Preview · {platform.label}</span>
    : <Seg icons value={phonePane} onChange={setPhonePane} options={[{ id: "post", label: "Post", icon: PenLine }, { id: "preview", label: "Preview", icon: Eye }]} />;
  /* THE PLATFORM: pills where there is room, a dropdown on a phone (her ask,
     2026-10-06) — three pills were a row of their own above the box. */
  const platformPick = twoPane
    ? SOCIAL_PLATFORMS.map((p) => <Choice key={p.id} on={platform.id === p.id} onClick={() => setNow({ platform: p.id })}>{p.label}</Choice>)
    : (
      <select value={platform.id} onChange={(e) => setNow({ platform: e.target.value })} aria-label="Platform"
        style={{ ...input, width: "auto", padding: "7px 10px", fontSize: 13, fontWeight: 600, color: B.teal }}>
        {SOCIAL_PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
      </select>
    );
  /* THE DAY IT IS FOR (her call, 2026-10-06) — the table sorts by it, and a
     send to GHL starts from it. Saves on the change, like any choice. */
  const dateInput = (
    <input type="date" aria-label="Post date" title="The day this post goes out" value={postDay(draft)}
      onChange={(e) => setNow({ scheduledDate: e.target.value || null })}
      style={{ ...input, width: "auto", padding: "6px 8px", fontSize: 12.5, color: postDay(draft) ? B.text : B.dim }} />
  );
  /* WIDE: the way back, the name and Done on ONE row — the drafting screen's
     one-row chrome, here because the box below wants the height.
     PHONE (her layout, 2026-10-06, matching the drafting bar on Outreach,
     Newsletters and Presentations — arrow on the left, pane icons on the
     right): the arrow, Draft / Done and the three panes as icons; the name,
     smaller and cut off, with the cog and the bin; the platform as a dropdown
     with Saved. Seven rows above the post became three short ones. */
  const heading = twoPane ? (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 16 }}>
      {back && <BarBack label={back.label} onClick={back.onClick} />}
      {titleInput}
      {status}
      {note}
      {family.length > 0 && <AlsoLine family={family} onOpen={onOpenPost} />}
    </div>
  ) : (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {back && (
          <button onClick={back.onClick} aria-label={String(back.label).replace(/^←\s*/, "")} title={String(back.label).replace(/^←\s*/, "")}
            style={{ ...btnGhost, padding: "7px 9px", display: "inline-flex", alignItems: "center" }}>
            <ArrowLeft size={16} />
          </button>
        )}
        {status}
        <span style={{ flex: 1 }} />
        {paneSeg}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6 }}>
        {titleInput}
        {cogBtn}
        {binBtn}
      </div>
      {note && <div style={{ display: "flex", marginTop: 2 }}>{note}</div>}
      {family.length > 0 && <div style={{ marginTop: 4 }}><AlsoLine family={family} onOpen={onOpenPost} /></div>}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
        {platformPick}
        {dateInput}
        <span style={{ flex: 1 }} />
        {savedTick}
      </div>
    </div>
  );

  /* What sits under the box on screen comes off its height: the picture line,
     the Brill line, and Brill's last reply once there is one. */
  const reserve = (twoPane ? BOX_RESERVE_WIDE : BOX_RESERVE_PHONE) + (picture ? PICTURE_LINE : 0) + BRILL_LINE + (lastBrill ? REPLY_LINE : 0);
  const boxHeight = twoPane ? `max(300px, calc(100vh - ${reserve}px))` : `max(200px, calc(100dvh - ${reserve}px))`;

  /* ── The composer ──────────────────────────────────────────────────────── */
  const composer = (
    <div style={{ minWidth: 0 }}>
      {twoPane && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          {platformPick}
          {dateInput}
          <span style={{ flex: 1 }} />
          {savedTick}
          {cogBtn}
          {binBtn}
        </div>
      )}

      {/* SETTINGS ALMOST NOBODY CHANGES LIVE, behind a cog (rule 8): the pillar
          it belongs to, the offer it serves (read by "Supported by" on the Offer
          Ladder), the meeting link its ask points at. Choices save on the press. */}
      {cog && (
        <div style={{ border: `1px solid ${B.border}`, borderRadius: 12, background: B.surface, padding: "16px 16px 2px", marginBottom: 14 }}>
          <Field label="Pillar">
            <select value={draft.inputs?.pillar || ""} onChange={(e) => setNow({ inputs: { ...(draft.inputs || {}), pillar: e.target.value || null } })} style={input}>
              <option value="">{pillars.length ? "Let Brill pick" : "No pillars locked yet"}</option>
              {pillars.map((p) => <option key={p.label} value={p.label}>{p.label}</option>)}
            </select>
          </Field>
          {/* HOOK STYLE, its own dropdown (her catch, 2026-09-25: "we might have
              lost hook style all together"). The old form had it as chips; the
              redesign only ever picked one silently. */}
          <Field label="Hook style">
            <select value={draft.inputs?.hookCategory || ""} onChange={(e) => setNow({ inputs: { ...(draft.inputs || {}), hookCategory: e.target.value || null } })} style={input}>
              <option value="">Let Brill pick</option>
              {SOCIAL_HOOK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Offer it supports">
            <select value={draft.offerId || ""} onChange={(e) => setNow({ offerId: e.target.value || null })} style={input}>
              <option value="">No offer</option>
              {offers.filter((o) => o.status !== "retired" || o.id === draft.offerId).map((o) => <option key={o.id} value={o.id}>{o.name || "Untitled offer"}</option>)}
            </select>
          </Field>
          <Field label="The ask">
            <AskSelect value={askOf(draft)} hasOffer={!!draft.offerId} ctas={usableCtas}
              onChange={(v) => setNow({ ctaId: v.startsWith("link:") ? v.slice(5) : null, inputs: { ...(draft.inputs || {}), cta: v.startsWith("link:") ? "library" : v } })} />
          </Field>
          <Field label="Length">
            <LengthChoice value={draft.inputs?.length || defaultLength(platform.id)}
              onChange={(v) => setNow({ inputs: { ...(draft.inputs || {}), length: v } })} />
          </Field>
          {/* On a phone Post as lives here, so the box's row stays one row. */}
          {!twoPane && crm && platformAccounts.length > 1 && (
            <Field label="Post as, in GHL">
              <select value={postAs} onChange={(e) => pickPostAs(e.target.value)} aria-label="Post as" style={input}>
                {platformAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}{a.type ? ` (${a.type})` : ""}</option>)}
              </select>
            </Field>
          )}
        </div>
      )}

      {error && (
        <div onClick={onDismissError} style={{ background: "var(--danger-quiet)", border: "1px solid var(--danger-line)", borderRadius: 9, padding: "11px 14px", marginBottom: 12, fontSize: 13, color: "var(--danger-text)", cursor: "pointer", lineHeight: 1.55 }}>
          <b>This post didn't save.</b> {error} Your words are still on screen.
        </div>
      )}

      <div style={{ border: `1px solid ${B.border2}`, borderRadius: 12, background: B.surface, overflow: "hidden" }}>
        {/* LIT: Brill's changes marked, until she taps in to carry on. The
            textarea stays mounted underneath, so nothing about her draft moves. */}
        {lit && (
          <div role="button" tabIndex={0} aria-label="Your post, with Brill's changes marked — tap to edit"
            onClick={() => { setLit(null); setTimeout(() => boxRef.current?.focus(), 0); }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setLit(null); setTimeout(() => boxRef.current?.focus(), 0); } }}
            style={{ ...input, border: "none", borderRadius: 0, height: boxHeight, overflowY: "auto", whiteSpace: "pre-wrap", lineHeight: 1.6, fontSize: 14, background: "transparent", cursor: "text" }}>
            {lit.filter((p) => p.type !== "cut").map((p, i) => (p.type === "add"
              ? <mark key={i} style={{ background: B.tealQuiet, color: "inherit", borderRadius: 3, padding: "0 1px" }}>{p.text}</mark>
              : <span key={i}>{p.text}</span>))}
          </div>
        )}
        <textarea ref={boxRef} value={caption} onChange={(e) => edit({ caption: e.target.value })} onBlur={commit}
          aria-label="Your post"
          placeholder={`Write your ${platform.label} post, or tell Brill below what it's about.`}
          style={{ ...input, border: "none", borderRadius: 0, minHeight: boxHeight, resize: "vertical", lineHeight: 1.6, fontSize: 14, background: "transparent", display: lit ? "none" : "block" }} />
        {picture && (
          /* The picture as an attachment line, not a banner (spec decision 2).
             It takes room only when it exists. */
          <PictureLine picture={picture} onAbout={setAbout} onDone={commit} onRemove={detach} />
        )}
        {picOpen && !picture && (
          <PictureSource crm={crm} onAttach={attach} onCancel={() => setPicOpen(false)} />
        )}
        <div style={{ display: "flex", alignItems: "center", gap: twoPane ? 8 : 6, padding: twoPane ? "8px 12px" : "8px 10px", borderTop: `1px solid ${B.border}`, background: B.surface2, flexWrap: "wrap" }}>
          <BrillButton items={brillMenu({ caption, hasPicture: !!picture, platformLabel: platform.label })}
            busy={tapBusy || visualBusy || sending} onPick={runTap} />
          {/* ONE ROW ON A PHONE (her ask, 2026-10-06): with GHL connected this
              row wrapped onto two more and pushed the Brill line down. So on a
              phone Picture and Send are icons (named for touch and screen
              readers), Copy says "Copy", and Post as moves into the cog. */}
          {!picture && (
            <button type="button" onClick={() => setPicOpen((v) => !v)} aria-expanded={picOpen} aria-label="Picture" title="Add a picture"
              style={{ ...quickBtn, display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", ...(twoPane ? {} : { padding: "6px 9px" }) }}>
              <ImageIcon size={13} />{twoPane && " Picture"}
            </button>
          )}
          <span style={{ fontSize: 12, color: over ? B.amber : B.dim }}>
            {caption.length.toLocaleString()}{twoPane || over ? ` / ${platform.limit.toLocaleString()}` : ""}{over ? ` — over ${platform.label}'s limit` : ""}
          </span>
          <span style={{ flex: 1 }} />
          {/* An icon on a phone too since Save as joined the row (2026-10-06) —
              the tick still says it copied. */}
          <button onClick={() => copy(caption, "post")} disabled={empty} aria-label={copied === "post" ? "Copied" : "Copy post"} title="Copy post"
            style={{ ...btnGhost, padding: twoPane ? "7px 14px" : "7px 10px", fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 6, opacity: empty ? 0.5 : 1, color: copied === "post" ? B.teal : btnGhost.color }}>
            {copied === "post" ? <Check size={13} /> : <Copy size={13} />}{twoPane && ` ${copied === "post" ? "Copied" : "Copy post"}`}
          </button>
          {onCreatePost && (
            <SaveAsButton open={saveAsOpen} setOpen={setSaveAsOpen} disabled={empty || !!saveAs?.busy}
              current={platform.id} onPick={saveAsPlatform} />
          )}
          {crm && twoPane && platformAccounts.length > 1 && (
            <select value={postAs} onChange={(e) => pickPostAs(e.target.value)} aria-label="Post as" title="Which account this goes to in GHL"
              style={{ ...input, width: "auto", padding: "6px 8px", fontSize: 12 }}>
              {platformAccounts.map((a) => <option key={a.id} value={a.id}>{a.name}{a.type ? ` (${a.type})` : ""}</option>)}
            </select>
          )}
          {crm && (
            <button onClick={() => setSendOpen(true)} disabled={empty || ghl?.busy}
              aria-label={ghl?.busy ? "Sending…" : draft.inputs?.ghlPostId ? "Update GHL draft" : "Send to GHL"}
              title={`${draft.inputs?.ghlPostId ? "Updates the draft in" : "Sends it to"} your GHL Social Planner as a draft. Nothing is posted.`}
              style={{ ...btnGhost, padding: twoPane ? "7px 14px" : "7px 10px", fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 6, opacity: empty ? 0.5 : 1 }}>
              <Send size={13} />{twoPane && ` ${ghl?.busy ? "Sending…" : draft.inputs?.ghlPostId ? "Update GHL draft" : "Send to GHL"}`}
            </button>
          )}
        </div>
      </div>
      {(ghl?.ok || ghl?.error) && (
        <div style={{ fontSize: 12.5, marginTop: 8, lineHeight: 1.5, color: ghl.ok ? B.teal : B.amber }}>
          {ghl.ok || ghl.error}
        </div>
      )}
      {/* "Kept" and "not kept" look identical until she comes back to a post
          and the conversation is gone — so a failed save is said. */}
      {chat.saveProblem && <div style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5, color: B.amber }}>{chat.saveProblem}</div>}
      {lastBrill && (
        <ReplyStrip turn={lastBrill} canUndo={!!rewrite?.canUndo && rewrite.turn === lastBrill} onUndo={undo}
          count={chat.turns.filter((t) => t.role !== "note").length} onOpen={() => setConvoOpen(true)} />
      )}
      <BrillLine inputRef={lineRef} value={chatText} onChange={setChatText} onSend={() => send(chatText)}
        busy={sending || visualBusy || tapBusy} placeholder={lineHint} />
      {sendOpen && (
        <SendPopup platformLabel={platform.label} startDay={postDay(draft)} again={!!draft.inputs?.ghlPostId}
          accounts={platformAccounts} postAs={postAs} onPostAs={pickPostAs}
          onSend={sendToGhl} onClose={() => setSendOpen(false)} />
      )}
      {saveAs && (
        saveAs.stage === "saved"
          ? <SaveAsPopup state={saveAs} onGo={() => { const p = saveAs.post; setSaveAs(null); if (p && onOpenPost) onOpenPost(p); }}
              onClose={() => setSaveAs(null)} />
          : <SaveAsSetup state={saveAs} onChange={(patch) => setSaveAs((cur) => (cur ? { ...cur, ...patch } : cur))}
              onWrite={() => writeSaveAs(saveAs)} onKeep={() => keepSaveAs(saveAs)} onClose={() => setSaveAs(null)} />
      )}
      {convoOpen && (
        <ChatScreen turns={chat.turns} caption={caption} sending={sending}
          onBack={goBackTo} onClose={() => setConvoOpen(false)}
          card={card?.fromChat ? <SuggestionCard card={card} current={caption} onUse={useCard} onKeep={() => setCard(null)} onChat={cardToChat} /> : null}
          line={<BrillLine inputRef={chatLineRef} autoFocus value={chatText} onChange={setChatText} onSend={() => send(chatText)}
            busy={sending || visualBusy || tapBusy} placeholder={lineHint} />} />
      )}
      {draftForm && (
        <DraftForm mode={draftForm} pillars={pillars} platformLabel={platform.label} library={libraryItems} ctas={usableCtas} hasPicture={!!picture} crm={crm}
          offers={offers.filter((o) => o.status !== "retired" || o.id === draft.offerId)}
          start={{ about: draft.inputs?.context || "", pillar: draft.inputs?.pillar || "", theme: draft.inputs?.theme || "", hook: draft.inputs?.hookCategory || "",
            offerId: draft.offerId || "", ask: askOf(draft), length: draft.inputs?.length || defaultLength(platform.id) }}
          onCancel={() => setDraftForm(null)} onDraft={submitDraft} />
      )}
      {libraryPick && (
        <LibraryPick library={libraryItems} pillar={draft.inputs?.pillar || ""} hasPicture={!!picture}
          onCancel={() => setLibraryPick(false)} onPick={pickFromLibrary} />
      )}

      {card && (
        <SuggestionCard card={card} current={caption} onUse={useCard} onKeep={() => setCard(null)} onChat={cardToChat} />
      )}
      {visualBusy && <div style={{ fontSize: 12.5, color: B.dim, marginTop: 10 }}>Making a quote card…</div>}
      {quoteOf(draft.visual) && <VisualCard visual={quoteOf(draft.visual)} />}

      {/* FIRST COMMENT — optional, its own Copy (spec: Brill does not post, so
          it is a box, not an action). COLLAPSED TO ONE LINE while empty (her
          ask, 2026-09-25): a picture made the post taller and pushed the box
          off the screen, and the way to it has to stay in sight. Pressing it
          gives the room; one with words in it is always open. */}
      {!String(draft.firstComment || "").trim() && !commentOpen ? (
        <button type="button" onClick={() => setCommentOpen(true)}
          style={{ marginTop: 10, background: "none", border: "none", padding: "4px 0", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: B.teal }}>
          + Add first comment <span style={{ color: B.dim, fontWeight: 400 }}>(optional — often the link)</span>
        </button>
      ) : (
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>First comment <span style={{ color: B.ghost, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>(optional — often the link)</span></div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <textarea value={draft.firstComment || ""} onChange={(e) => edit({ firstComment: e.target.value })}
              onBlur={() => { commit(); if (!String(latest.current.firstComment || "").trim()) setCommentOpen(false); }}
              aria-label="First comment" rows={3} autoFocus={commentOpen && !String(draft.firstComment || "").trim()}
              placeholder="What you'll post as the first comment"
              style={{ ...input, flex: 1, resize: "vertical", lineHeight: 1.5 }} />
            <button onClick={() => copy(draft.firstComment || "", "comment")} disabled={!String(draft.firstComment || "").trim()}
              style={{ ...btnGhost, padding: "9px 12px", fontSize: 12.5, flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, opacity: String(draft.firstComment || "").trim() ? 1 : 0.5, color: copied === "comment" ? B.teal : btnGhost.color }}>
              {copied === "comment" ? <Check size={13} /> : <Copy size={13} />} {copied === "comment" ? "Copied" : "Copy comment"}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const preview = (
    <PostPreview post={draft} platform={platform}
      name={userSettings.businessName || userSettings.displayName || "You"} photoUrl={userSettings.photoUrl} />
  );

  /* Two panes where there is room — the post with Brill under it, and its
     preview — and one at a time on a phone. */
  if (!twoPane) {
    return (
      <>
        {heading}
        {phonePane === "post" ? composer : preview}
      </>
    );
  }
  return (
    <>
      {heading}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1.5fr) minmax(0, 1fr)", gap: 18, alignItems: "start" }}>
        {composer}
        <div style={{ minWidth: 0 }}>
          <div style={{ marginBottom: 12 }}>{paneSeg}</div>
          {preview}
        </div>
      </div>
    </>
  );
}


/* ── The Brill button ────────────────────────────────────────────────────────
   Teal, because it is the action (rule 5). Its menu is `brillMenu` — what the
   box holds decides what is offered. Closes on a pick, on a click anywhere
   else, and on Escape. */
/* A PLAIN LIST, NOT TILES (her read, 2026-09-25: "this feels overwhelming").
   Nine icon tiles with a sentence each read like a form to fill in, for what
   is one quick pick. Names only, the sentence as a hover tip; a rule before
   the three that are bigger moves than an edit. */
const BIG_MOVES = new Set(["quote", "redraft", "ask"]);

function BrillButton({ items, busy, onPick }) {
  const { ref, anchor, open, toggle, close } = useAnchoredPanel();
  const wrap = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (!wrap.current?.contains(e.target)) close(); };
    const esc = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <span ref={wrap} style={{ position: "relative", display: "inline-block" }}>
      <button type="button" ref={ref} onClick={() => !busy && toggle()} disabled={busy} aria-haspopup="menu" aria-expanded={open}
        style={{ ...quickBtn, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6, opacity: busy ? 0.6 : 1, cursor: busy ? "default" : "pointer" }}>
        <Sparkles size={13} /> {busy ? "Brill is working…" : "Brill"}
      </button>
      {open && (
        <div role="menu" aria-label="Brill"
          style={{ ...floatingPanel(anchor, { width: 230, wants: items.length * 34 + 20 }), background: B.surface, border: `1px solid ${B.border2}`, borderRadius: 10, padding: 5, boxShadow: "var(--shadow-menu)" }}>
          {items.map((it, i) => (
            <React.Fragment key={it.id}>
              {BIG_MOVES.has(it.id) && !BIG_MOVES.has(items[i - 1]?.id) && i > 0 && (
                <div style={{ height: 1, background: B.border, margin: "4px 6px" }} />
              )}
              <button type="button" role="menuitem" title={it.desc}
                onClick={() => { close(); onPick(it); }}
                style={{ display: "block", width: "100%", textAlign: "left", padding: "7px 10px", borderRadius: 6, border: "none", background: "transparent", fontFamily: "inherit", fontSize: 13, color: B.text, cursor: "pointer" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = B.tealQuiet; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                {it.label}
              </button>
            </React.Fragment>
          ))}
        </div>
      )}
    </span>
  );
}


/* ── Draft it for me / Redraft from scratch ──────────────────────────────────
   A pop-up with Cancel, the one Cancel the pattern allows (rule 8, spec
   section 4). What it's about first; pillar and hook style as dropdowns that
   default to "Let Brill pick" (spec decision 4: ask one thing, default the
   rest — her call, 2026-09-25, is that the rest must still be CHOOSABLE). */
/* THE ASK, one dropdown (her model, 2026-09-25). Learn more needs an offer to
   be about, so it is only offered with one. Book a call lists her meeting
   links — a strategy call, a coffee chat — and works with or without an offer. */
export function AskSelect({ value, onChange, hasOffer, ctas = [] }) {
  const v = value === "learn" && !hasOffer ? "none" : value;
  const calls = ctas.filter((c) => c.type === "call");
  const others = ctas.filter((c) => c.type !== "call");
  return (
    <select value={v} onChange={(e) => onChange(e.target.value)} aria-label="The ask" style={input}>
      <option value="none">No ask — end naturally</option>
      <option value="message">Comment or message me</option>
      {hasOffer && <option value="learn">Learn more about the offer (link in the comments)</option>}
      {/* BY TYPE (her catch, 2026-09-25: a landing page sat under "Book a
          call"). Meeting Links holds booking links, landing pages and other
          tools; only a booking link is a call. The rest are still a place to
          send people, so they keep a group of their own. */}
      {calls.length > 0 && (
        <optgroup label="Book a call (link in the comments)">
          {calls.map((c) => <option key={c.id} value={`link:${c.id}`}>{c.name || c.buttonText || "Untitled link"}</option>)}
        </optgroup>
      )}
      {others.length > 0 && (
        <optgroup label="Send them to a link (in the comments)">
          {others.map((c) => <option key={c.id} value={`link:${c.id}`}>{c.name || c.buttonText || "Untitled link"}</option>)}
        </optgroup>
      )}
    </select>
  );
}

/* PILLARS AS CARDS, THEMES AS CHIPS UNDER THE ONE PICKED — the old
   generator's layout, back by her ask (2026-09-25: "could we go back to this
   style?"). A card says what the pillar is FOR, which a dropdown hid; the
   themes are its sub-items, so they open inside it. Pressing the picked card
   or chip again un-picks it, and nothing picked is "Let Brill pick". */
export function PillarPicker({ pillars = [], value, theme, onPillar, onTheme }) {
  if (!pillars.length) return <div style={{ fontSize: 12.5, color: B.dim }}>No pillars locked yet — Brill picks from your foundation.</div>;
  return (
    <div role="radiogroup" aria-label="Content pillar" style={{ display: "grid", gap: 6 }}>
      <button type="button" role="radio" aria-checked={!value} onClick={() => onPillar("")}
        style={{ justifySelf: "start", padding: "4px 11px", borderRadius: 14, fontSize: 12, fontFamily: "inherit", cursor: "pointer",
          border: `1px solid ${!value ? B.teal : B.border2}`, background: !value ? B.tealQuiet : "transparent", color: !value ? B.teal : B.dim, fontWeight: !value ? 700 : 500 }}>
        Let Brill pick
      </button>
      {pillars.map((p) => {
        const on = value === p.label;
        return (
          <div key={p.label} style={{ border: `1px solid ${on ? B.teal : B.border}`, borderRadius: 10, background: on ? B.tealQuiet : B.surface, overflow: "hidden" }}>
            <button type="button" role="radio" aria-checked={on} onClick={() => onPillar(on ? "" : p.label)}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 12px", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit" }}>
              <div style={{ fontSize: 13.5, fontWeight: 700, color: on ? B.teal : B.text }}>{p.label}</div>
              {p.subtitle && <div style={{ fontSize: 11.5, color: B.dim, marginTop: 2, lineHeight: 1.4 }}>{p.subtitle}</div>}
            </button>
            {on && (p.themes || []).length > 0 && (
              <div style={{ borderTop: `1px solid ${B.tealLine}`, padding: "8px 12px 10px" }}>
                <div style={{ fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: B.label, fontWeight: 700, marginBottom: 6 }}>
                  Theme <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: B.dim }}>(optional)</span>
                </div>
                <div role="radiogroup" aria-label="Theme" style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {p.themes.map((t) => {
                    const tOn = theme === t;
                    return (
                      <button key={t} type="button" role="radio" aria-checked={tOn} onClick={() => onTheme(tOn ? "" : t)}
                        style={{ padding: "4px 11px", borderRadius: 14, fontSize: 12, fontFamily: "inherit", cursor: "pointer",
                          border: `1px solid ${tOn ? B.teal : B.border2}`, background: tOn ? B.surface : "transparent", color: tOn ? B.teal : B.mid, fontWeight: tOn ? 700 : 500 }}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* Short / Medium / Long, the range under each (her ask, 2026-09-25). `extra`
   adds choices shaped the same — an arc's Mixed (2026-09-30). */
export function LengthChoice({ value, onChange, extra = [], options = null }) {
  return (
    <div role="radiogroup" aria-label="Length" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {(options || [...POST_LENGTHS, ...extra]).map((l) => {
        const on = value === l.id;
        return (
          <button key={l.id} type="button" role="radio" aria-checked={on} onClick={() => onChange(l.id)}
            style={{ flex: "1 1 90px", padding: "7px 10px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", textAlign: "center",
              border: `1px solid ${on ? B.teal : B.border2}`, background: on ? B.tealQuiet : "transparent" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: on ? B.teal : B.text }}>{l.label}</div>
            <div style={{ fontSize: 11, color: B.dim, marginTop: 1 }}>{l.words}</div>
          </button>
        );
      })}
    </div>
  );
}

/* `aboutFromLibrary` — what a picked item says in "What's it about?" — lives
   in utils/library.js, where an arc's posts reach it too. */
export { aboutFromLibrary };

/* "FROM YOUR LIBRARY" — THE LIST, for the Draft pop-up and the arc's setup
   (her call, 2026-09-29: the arc uses "the SAME list … with checkboxes").
   Every kind, then pick (2026-09-28). Unused first; within that, an idea filed
   under the pillar she has picked floats up. A post is public: a testimonial
   without her permission is SHOWN and not pickable, so she knows why.
     · without `picked` a row is one press and the caller closes the list;
     · with `picked` (ids) each row is a checkbox and the list stays open.
   `whyNot(item)` is the caller's own reason a row cannot be taken right now. */
export function LibraryChoices({ library = [], pillar = "", picked = null, whyNot = null, kinds = null, onPick }) {
  const [only, setOnly] = useState("all");
  const multi = Array.isArray(picked);
  // `kinds` narrows the list and its filter chips (Pick from library: her own words only).
  const shownKinds = kinds ? LIBRARY_KINDS.filter((k) => kinds.includes(k.id)) : LIBRARY_KINDS;
  const options = libraryView(kinds ? library.filter((i) => kinds.includes(i.kind)) : library, { kind: only, sort: "unused" })
    .sort((a, b) => (isUnused(a) === isUnused(b) ? (a.tag === pillar && pillar ? 0 : 1) - (b.tag === pillar && pillar ? 0 : 1) : 0));
  return (
    <div style={{ border: `1px solid ${B.border2}`, borderRadius: 10, marginBottom: 10 }}>
      <div role="radiogroup" aria-label="Which kind" style={{ display: "flex", gap: 5, flexWrap: "wrap", padding: "8px 10px" }}>
        {["all", ...shownKinds.map((k) => k.id)].map((k) => {
          const on = only === k;
          return (
            <button key={k} type="button" role="radio" aria-checked={on} onClick={() => setOnly(k)}
              style={{ padding: "3px 10px", borderRadius: 14, fontSize: 11.5, fontFamily: "inherit", cursor: "pointer",
                border: `1px solid ${on ? B.teal : B.border2}`, background: on ? B.tealQuiet : "transparent", color: on ? B.teal : B.mid, fontWeight: on ? 700 : 500 }}>
              {k === "all" ? "All" : LIBRARY_KINDS.find((x) => x.id === k).plural}
            </button>
          );
        })}
      </div>
      <div style={{ maxHeight: 220, overflowY: "auto" }}>
        {!options.length ? (
          <div style={{ padding: "12px", fontSize: 12.5, color: B.dim, lineHeight: 1.6, borderTop: `1px solid ${B.border}` }}>Nothing here yet. Jot it down in Run → Library and it'll show up here.</div>
        ) : options.map((i) => {
          const on = multi && picked.includes(i.id);
          const noPermission = needsPermission(i);
          /* A ticked row can always be unticked, whatever the cap says. */
          const reason = noPermission ? "Tick permission in the Library before using this publicly" : (!on && whyNot ? whyNot(i) : null);
          const blocked = !!reason;
          return (
            <button key={i.id} type="button" onClick={() => !blocked && onPick(i)} disabled={blocked} title={reason || undefined}
              {...(multi ? { role: "checkbox", "aria-checked": on } : {})}
              style={{ display: "flex", gap: 9, alignItems: "flex-start", width: "100%", textAlign: "left", padding: "8px 12px", background: on ? B.tealQuiet : "none", border: "none", borderTop: `1px solid ${B.border}`, cursor: blocked ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: blocked ? 0.55 : 1 }}>
              {multi && (
                <span aria-hidden="true" style={{ width: 15, height: 15, marginTop: 2, borderRadius: 4, flexShrink: 0, border: `1.5px solid ${on ? B.teal : B.border2}`, background: on ? B.teal : "transparent", color: "var(--btn-text)", fontSize: 11, lineHeight: "13px", textAlign: "center", fontWeight: 700 }}>{on ? "✓" : ""}</span>
              )}
              <span style={{ minWidth: 0, flex: 1 }}>
                <span style={{ display: "block", fontSize: 11, color: i.kind === "idea" && i.tag === pillar && pillar ? B.teal : B.dim, fontWeight: 700, marginBottom: 2 }}>
                  {LIBRARY_KINDS.find((k) => k.id === i.kind)?.label || "Idea"} · {metaFor(i)}<span style={{ fontWeight: 400, color: B.faint }}> · {usedLabel(i)}{noPermission ? " · no permission yet" : ""}</span>
                </span>
                <span style={{ fontSize: 12.5, color: B.text, lineHeight: 1.45, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{i.title ? `${i.title}: ` : ""}{i.text}</span>
              </span>
              {multi && pickedPicture(i) && <img src={pickedPicture(i)} alt="" title="Its picture comes with it" style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 5, flexShrink: 0 }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* "PICK FROM LIBRARY" — the pop-up (her design, 2026-10-01). Her ideas and
   stories only: those are her words; a testimonial is a client's and an
   objection is a question. One press puts it in the box as it is. */
export function LibraryPick({ library = [], pillar = "", hasPicture = false, onCancel, onPick }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onCancel]);
  return (
    <div role="dialog" aria-modal="true" aria-label="Pick from library"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 540, maxHeight: "calc(100vh - 32px)", overflowY: "auto", background: B.surface, borderRadius: 14, border: `1px solid ${B.border2}`, boxShadow: "var(--shadow-menu)", padding: "20px 20px 16px" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 4 }}>Pick from library</div>
        <div style={{ fontSize: 12.5, color: B.dim, marginBottom: 14, lineHeight: 1.5 }}>
          Your words go into the post exactly as you wrote them{hasPicture ? "" : ", with the picture if it has one"}. Brill isn't involved. To tidy them, use Brill → Tighten or Fix spelling.
        </div>
        <LibraryChoices library={library} pillar={pillar} kinds={OWN_WORDS_KINDS} onPick={onPick} />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="button" onClick={onCancel} style={{ ...btnGhost, padding: "8px 14px", fontSize: 13 }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export function DraftForm({ mode, pillars = [], platformLabel, library = [], ctas = [], offers = [], start = {}, hasPicture = false, crm = false, onCancel, onDraft }) {
  const [about, setAbout] = useState(start.about || "");
  /* THE PICTURE, IN THE POP-UP (spec A3, built 2026-10-06): only while the
     post has none — one picked here is attached before Brill drafts, so its
     "what's in it" line reaches Brill as a filter, never the subject. */
  const [pic, setPic] = useState(null);
  const [picOpen, setPicOpen] = useState(false);
  const [pl, setPl] = useState(pillars.some((p) => p.label === start.pillar) ? start.pillar : "");
  const pillarObj = pillars.find((p) => p.label === pl) || null;
  const [theme, setTheme] = useState(start.theme || "");
  const [hk, setHk] = useState(SOCIAL_HOOK_CATEGORIES.some((c) => c.id === start.hook) ? start.hook : "");
  const [offerId, setOfferId] = useState(offers.some((o) => o.id === start.offerId) ? start.offerId : "");
  const [ask, setAsk] = useState(start.ask && (!start.ask.startsWith("link:") || ctas.some((c) => `link:${c.id}` === start.ask)) ? start.ask : "none");
  const [length, setLength] = useState(start.length || "medium");
  const [idea, setIdea] = useState(null);
  const [ideasOpen, setIdeasOpen] = useState(false);
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [onCancel]);
  const themes = pillarObj?.themes || [];
  const redraft = mode === "redraft";
  const pickIdea = (i) => {
    setIdea(i);
    setAbout(aboutFromLibrary(i));
    /* An idea is filed under a pillar; the other kinds' `tag` is themes or
       tags, never a pillar, so only an idea moves the pillar. */
    if (i.kind === "idea" && i.tag && pillars.some((p) => p.label === i.tag) && i.tag !== pl) { setPl(i.tag); setTheme(""); }
    setIdeasOpen(false);
  };
  const unused = library.filter((i) => isUnused(i)).length;
  const submit = () => onDraft({
    about: about.trim(), pillar: pl, theme: themes.includes(theme) ? theme : "", hook: hk,
    offerId: offerId || null, ask: ask === "learn" && !offerId ? "none" : ask, length,
    idea,
    picture: pic?.url ? { url: pic.url, about: String(pic.about || "").trim() } : null,
  });
  return (
    <div role="dialog" aria-modal="true" aria-label={redraft ? "Redraft from scratch" : "Draft it for me"}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      style={{ position: "fixed", inset: 0, zIndex: 200, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ width: "100%", maxWidth: 540, maxHeight: "calc(100vh - 32px)", overflowY: "auto", background: B.surface, borderRadius: 14, border: `1px solid ${B.border2}`, boxShadow: "var(--shadow-menu)", padding: "20px 20px 16px" }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: B.text, marginBottom: 4 }}>{redraft ? "Redraft from scratch" : `Draft a ${platformLabel} post`}</div>
        {redraft && <div style={{ fontSize: 12.5, color: B.dim }}>A new post. Yours stays until you choose.</div>}
        <div style={{ marginTop: 14 }}>
          <Field label="What's it about?">
            {/* FROM AN IDEA (her ask, 2026-09-25), sitting with the field it
                fills. Same-pillar ideas float up; unused before used. */}
            {idea ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, padding: "6px 10px", background: B.tealQuiet, border: `1px solid ${B.tealLine}`, borderRadius: 8, fontSize: 12 }}>
                <Lightbulb size={13} color={B.teal} /> <span style={{ color: B.mid }}>From your library · {LIBRARY_KINDS.find((k) => k.id === idea.kind)?.label || "Idea"}</span>
                {/* SAY THE PICTURE IS COMING before she drafts (her catch,
                    2026-09-29) — or that her own stays, when the post has one. */}
                {pickedPicture(idea) && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: B.mid }}>
                    <img src={pickedPicture(idea)} alt="" style={{ width: 22, height: 22, objectFit: "cover", borderRadius: 4 }} />
                    {hasPicture ? "your post keeps its own picture" : "and its picture"}
                  </span>
                )}
                <button type="button" onClick={() => setIdea(null)} aria-label="Not from this item" title="Not from this item"
                  style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: B.dim, padding: 2, display: "flex" }}><X size={13} /></button>
              </div>
            ) : (
              <button type="button" onClick={() => setIdeasOpen((v) => !v)}
                style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, padding: 0, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: B.teal }}>
                <Lightbulb size={13} /> From your library
                {unused > 0 && <span style={{ color: B.dim, fontWeight: 400 }}>({unused} unused)</span>}
              </button>
            )}
            {ideasOpen && !idea && <LibraryChoices library={library} pillar={pl} onPick={pickIdea} />}
            <textarea value={about} onChange={(e) => setAbout(e.target.value)} autoFocus rows={3}
              aria-label="What's it about?" placeholder="A real moment, person, or situation behind it. One specific detail makes a noticeably better post."
              style={{ ...input, resize: "vertical", lineHeight: 1.5 }} />
          </Field>
          {!hasPicture && (
            <Field label="Picture (optional)">
              {pic ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <img src={pic.url} alt="" style={{ width: 44, height: 44, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />
                  <input value={pic.about} onChange={(e) => setPic({ ...pic, about: e.target.value })} aria-label="What's in the picture"
                    placeholder="What's in it, and why does it matter?" style={{ ...input, flex: 1, minWidth: 0, fontSize: 12.5 }} />
                  <button type="button" onClick={() => setPic(null)} aria-label="Remove the picture" title="Remove the picture"
                    style={{ background: "none", border: "none", cursor: "pointer", color: B.dim, padding: 2, display: "flex" }}><X size={14} /></button>
                </div>
              ) : picOpen ? (
                <div style={{ border: `1px solid ${B.border}`, borderRadius: 10, overflow: "hidden" }}>
                  <PictureSource crm={crm} onAttach={(url) => { const u = String(url || "").trim(); if (u) { setPic({ url: u, about: "" }); setPicOpen(false); } }} onCancel={() => setPicOpen(false)} />
                </div>
              ) : (
                <button type="button" onClick={() => setPicOpen(true)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: 0, background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: B.teal }}>
                  <ImageIcon size={13} /> Add a picture
                </button>
              )}
            </Field>
          )}
          <Field label="Content pillar">
            <PillarPicker pillars={pillars} value={pl} theme={theme}
              onPillar={(v) => { setPl(v); setTheme(""); }} onTheme={setTheme} />
          </Field>
          <Field label="Hook style">
            <select value={hk} onChange={(e) => setHk(e.target.value)} aria-label="Hook style" style={input}>
              <option value="">Let Brill pick</option>
              {SOCIAL_HOOK_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </Field>
          {/* WHAT IT'S ABOUT, then WHAT IT ASKS — two questions, because a post
              about her offer can still ask for a strategy call. */}
          <Field label="Offer it's about">
            <select value={offerId} onChange={(e) => setOfferId(e.target.value)} aria-label="Offer it's about" style={input}>
              <option value="">No offer</option>
              {offers.map((o) => <option key={o.id} value={o.id}>{o.name || "Untitled offer"}</option>)}
            </select>
          </Field>
          <Field label="The ask">
            <AskSelect value={ask} onChange={setAsk} hasOffer={!!offerId} ctas={ctas} />
          </Field>
          <Field label="Length">
            <LengthChoice value={length} onChange={setLength} />
          </Field>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 6 }}>
          <button type="button" onClick={onCancel} style={{ ...btnGhost, padding: "8px 14px", fontSize: 13 }}>Cancel</button>
          <button type="button" onClick={submit}
            style={{ ...quickBtn, padding: "8px 16px", fontSize: 13, fontWeight: 700 }}>{redraft ? "Redraft it" : "Draft it"}</button>
        </div>
      </div>
    </div>
  );
}


/* ── The picture, attached ───────────────────────────────────────────────────
   A slim line, never a banner (spec decision 2): the thumbnail, what's in it,
   and a way to take it off. "What's in it, and why does it matter?" is the
   spec's question — it is what "Work in the picture" hands Brill, and the
   alt text. A link that draws nothing says so, because a broken picture and a
   working one look the same until something tries to show it. */
export function PictureLine({ picture, onAbout, onDone, onRemove }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [picture.url]);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderTop: `1px solid ${B.border}` }}>
      {broken
        ? <span style={{ width: 40, height: 40, borderRadius: 6, background: B.amberQuiet, border: `1px solid ${B.amberLine}`, flexShrink: 0 }} />
        : <img src={picture.url} alt={picture.alt || ""} onError={() => setBroken(true)} style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6, flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <input value={picture.about || ""} onChange={(e) => onAbout(e.target.value)} onBlur={onDone}
          aria-label="What's in the picture" placeholder="What's in it, and why does it matter?"
          style={{ ...input, padding: "6px 10px", fontSize: 12.5 }} />
        {broken && (
          <div style={{ fontSize: 11.5, color: B.amber, marginTop: 4, lineHeight: 1.5 }}>
            Nothing loaded from that link. It may need to be public, or it may be the page the picture sits on rather than the picture itself.
          </div>
        )}
      </div>
      <IconBtn icon={X} label="Remove the picture" onClick={onRemove} size={14} />
    </div>
  );
}

/* WHERE A PICTURE COMES FROM. Her GHL library where it is connected (the
   Settings logo's picker, which uploads too); a pasted link everywhere — "not
   everyone has GHL" (her rule, 2026-09-23), so the link is never the fallback
   that only shows when something failed. Canva joins this row when it exists
   and never shows as a dead button before then (spec). */
export function PictureSource({ crm, onAttach, onCancel, remember = null }) {
  const [link, setLink] = useState("");
  const ok = /^https?:\/\/\S+$/i.test(link.trim());
  return (
    <div style={{ padding: "10px 12px", borderTop: `1px solid ${B.border}` }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input value={link} onChange={(e) => setLink(e.target.value)} type="url" autoFocus
          onKeyDown={(e) => { if (e.key === "Enter" && ok) onAttach(link); if (e.key === "Escape") onCancel(); }}
          aria-label="Link to a picture" placeholder="Paste a link to a picture"
          style={{ ...input, flex: "1 1 200px", minWidth: 0, padding: "7px 11px", fontSize: 12.5 }} />
        <button type="button" onClick={() => onAttach(link)} disabled={!ok}
          style={{ ...quickBtn, opacity: ok ? 1 : 0.5, cursor: ok ? "pointer" : "default" }}>Attach</button>
        <MediaPickButton onPick={onAttach} label="From my library" remember={remember} />
        <button type="button" onClick={onCancel} style={{ ...btnGhost, padding: "6px 10px", fontSize: 12 }}>Cancel</button>
      </div>
    </div>
  );
}


/* ── What a one-tap came back with ───────────────────────────────────────────
   Under the box, never in it (rule 4, spec decision 6): cut words struck, new
   words marked, and her choice — Use this / Keep mine. The preview keeps
   showing HER words the whole time. A card made from words she has since
   changed says so, in amber, before Use this replaces them. */
/* ── Save as another platform (her idea and her labels, 2026-10-06) ─────────
   Beside Copy, because it is the same thought: "I want this somewhere else."
   Her words for the menu: "Save as Instagram", not "Instagram version". The
   platform the post is already for is not offered — that is "Fit for …" in
   the Brill menu, which reshapes this post instead of making another. */
function SaveAsButton({ open, setOpen, disabled, current, onPick }) {
  const items = [
    ...SOCIAL_PLATFORMS.filter((p) => p.id !== current).map((p) => [
      <PlatformIcon id={p.id} size={16} />,
      `Save as ${p.label}`,
      `Brill writes it for ${p.label} as a new post — the same story${p.id === "instagram" ? ", with hashtags" : ""}.`,
      false, () => onPick(p.id),
    ]),
    [<Copy size={14} />, "Save as a plain copy", "The same words as a new post. No Brill.", false, () => onPick("copy")],
  ];
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" onClick={() => setOpen(!open)} disabled={disabled} aria-expanded={open} aria-haspopup="menu"
        style={{ ...btnGhost, padding: "7px 10px", fontSize: 12.5, display: "inline-flex", alignItems: "center", gap: 4, opacity: disabled ? 0.5 : 1,
          ...(open ? { color: B.teal, border: `1px solid ${B.tealLine}`, background: B.tealQuiet } : {}) }}>
        Save as <ChevronDown size={13} />
      </button>
      {open && (
        <>
          {/* Menu2 anchors to the element just before it, so it follows the
              button directly; the click-away layer comes after. */}
          <Menu2 head="A new post from this one" items={items.map(([ico, name, desc, dis, go]) => [ico, name, desc, dis, () => { setOpen(false); go(); }])} />
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
        </>
      )}
    </span>
  );
}

/* SAVE AS, BEFORE IT IS WRITTEN (her ask, 2026-10-09). How long, the hook,
   a line of her own, then Write it. Brill's version lands here, above the
   choices, so she can change any of them and Regenerate; nothing is a post
   until Save it. The one Cancel in the pattern is a dialog's (rule 8). Each
   Write / Regenerate is one Brill call. */
function SaveAsSetup({ state, onChange, onWrite, onKeep, onClose }) {
  const label = platformOf(state.platform).label;
  const written = state.stage === "preview" && state.words;
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape" && !state.busy) onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [state.busy, onClose]);
  const hooks = [{ id: "keep", label: "Keep my hook" }, ...SOCIAL_HOOK_CATEGORIES];
  return (
    <>
      <div onClick={state.busy ? undefined : onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,8,25,0.42)", zIndex: 60 }} />
      <div role="dialog" aria-label={`Save as ${label}`} style={{ position: "fixed", zIndex: 61, left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "min(520px, calc(100vw - 32px))",
        maxHeight: "calc(100vh - 32px)", overflowY: "auto",
        background: B.surface, borderRadius: 16, boxShadow: "var(--shadow-menu)", border: `1px solid ${B.border2}`, padding: 18, display: "flex", flexDirection: "column", gap: 14 }}>
        <b style={{ fontSize: 16, color: B.text, display: "flex", alignItems: "center", gap: 8 }}>
          <PlatformIcon id={state.platform} size={16} /> Save as {label}
        </b>
        {written && (
          <div style={{ border: `1px solid ${B.border}`, borderRadius: 12, background: B.surface2, padding: "10px 12px", fontSize: 13, lineHeight: 1.55, color: B.text, whiteSpace: "pre-wrap", maxHeight: 240, overflowY: "auto", opacity: state.busy ? 0.5 : 1 }}>
            {state.words}
          </div>
        )}
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: B.mid, marginBottom: 6 }}>How long</div>
          <LengthChoice value={state.length} onChange={(v) => onChange({ length: v })} options={saveAsLengths(state.platform)} />
        </div>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: B.mid, marginBottom: 6 }}>Hook</div>
          <div role="radiogroup" aria-label="Hook" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {hooks.map((h) => {
              const on = state.hook === h.id;
              return (
                <button key={h.id} type="button" role="radio" aria-checked={on} onClick={() => onChange({ hook: h.id })} title={h.description || undefined}
                  style={{ padding: "6px 11px", borderRadius: 18, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: on ? 700 : 500,
                    border: `1px solid ${on ? B.teal : B.border2}`, background: on ? B.tealQuiet : "transparent", color: on ? B.teal : B.text }}>
                  {h.label}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          <label htmlFor="saveas-note" style={{ display: "block", fontSize: 12, fontWeight: 700, color: B.mid, marginBottom: 6 }}>Anything else?</label>
          <input id="saveas-note" value={state.note || ""} onChange={(e) => onChange({ note: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter" && !state.busy) onWrite(); }}
            placeholder="e.g. a little punchier" style={{ ...input, width: "100%", boxSizing: "border-box" }} />
        </div>
        {state.error && <div role="alert" style={{ fontSize: 13, color: B.amber, lineHeight: 1.5 }}>{state.error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
          <button type="button" onClick={onClose} disabled={state.busy} style={{ ...btnGhost, padding: "9px 14px", fontSize: 13 }}>Cancel</button>
          {written ? (
            <>
              <button type="button" onClick={onWrite} disabled={state.busy} style={{ ...btnGhost, padding: "9px 14px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Sparkles size={13} color={B.teal} /> {state.busy ? "Writing…" : "Regenerate"}
              </button>
              <button type="button" onClick={onKeep} disabled={state.busy} style={{ ...quickBtn, padding: "9px 16px", fontSize: 13, fontWeight: 700 }}>Save it</button>
            </>
          ) : (
            <button type="button" onClick={onWrite} disabled={state.busy} style={{ ...quickBtn, padding: "9px 16px", fontSize: 13, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Sparkles size={13} /> {state.busy ? `Writing your ${label} post…` : "Write it"}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

/* After Save as: written — go to it, or stay (her call: "save as can pop up a
   go to the instagram post"). Says it is its own post, because "one won't
   change the other" is the thing to know about it. */
function SaveAsPopup({ state, onGo, onClose }) {
  const label = platformOf(state.platform).label;
  const pic = pictureOf(state.post?.visual);
  const needsPicture = state.post && state.platform === "instagram" && !pic;
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape" && !state.busy) onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [state.busy, onClose]);
  return (
    <>
      <div onClick={state.busy ? undefined : onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,8,25,0.42)", zIndex: 60 }} />
      <div role="dialog" aria-label={state.plain ? "Copy saved" : `${label} post`} style={{ position: "fixed", zIndex: 61, left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "min(420px, calc(100vw - 32px))",
        background: B.surface, borderRadius: 16, boxShadow: "var(--shadow-menu)", border: `1px solid ${B.border2}`, padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        {state.busy ? (
          <div style={{ fontSize: 14, color: B.text, display: "flex", alignItems: "center", gap: 10 }}>
            <Sparkles size={16} color={B.teal} /> {state.plain ? "Saving a copy…" : `Writing your ${label} post…`}
          </div>
        ) : state.error ? (
          <>
            <div style={{ fontSize: 14, color: B.amber, lineHeight: 1.5 }}>{state.error}</div>
            <button type="button" onClick={onClose} style={{ ...btnGhost, padding: "10px 14px" }}>Close</button>
          </>
        ) : (
          <>
            <b style={{ fontSize: 16, color: B.text, display: "flex", alignItems: "center", gap: 8 }}>
              <Check size={16} color={B.teal} /> {state.plain ? "Your copy is saved" : `Your ${label} post is written`}
            </b>
            <div style={{ display: "flex", gap: 10, border: `1px solid ${B.border}`, borderRadius: 12, background: B.surface2, padding: "10px 12px" }}>
              {pic && <img src={pic.url} alt={pic.alt || ""} style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 8, flexShrink: 0 }} />}
              <div style={{ fontSize: 12.5, lineHeight: 1.5, color: B.text, whiteSpace: "pre-wrap", maxHeight: 96, overflow: "hidden" }}>{String(state.post?.caption || "").slice(0, 260)}</div>
            </div>
            <div style={{ fontSize: 12, color: B.dim, lineHeight: 1.5 }}>It's its own post{pic ? ", with your picture" : ""}. Changing one won't change the other.</div>
            {needsPicture && <div style={{ fontSize: 12, color: B.amber, lineHeight: 1.5 }}>Instagram needs a picture — add one on the new post.</div>}
            <button type="button" onClick={onGo} style={{ border: "none", borderRadius: 12, background: "var(--accent)", color: "var(--btn-text)", fontFamily: "inherit", fontWeight: 700, fontSize: 14.5, padding: 13, cursor: "pointer" }}>
              {state.plain ? "Go to the copy" : `Go to the ${label} post`}
            </button>
            <button type="button" onClick={onClose} style={{ ...btnGhost, padding: "11px 14px", fontSize: 14 }}>Stay on this one</button>
          </>
        )}
      </div>
    </>
  );
}

/* SEND TO GHL ASKS WHEN (her call, 2026-10-06: "if it pushed to GHL it has a
   date"). The post's own day, else today; the time she last used. A moment
   already past is refused here, because GHL would file the draft on "now".
   It says, before she presses, that sending marks the post Done. */
function SendPopup({ platformLabel, startDay, again, accounts = [], postAs, onPostAs, onSend, onClose }) {
  /* Opens on a moment it can send: the post's day if that is still ahead at
     her usual time, else today, else tomorrow — never on "already passed". */
  const [time, setTime] = useState(readSendTime);
  const [day, setDay] = useState(() => [startDay, todayIso(), addDays(todayIso(), 1)].find((d) => d && !sendMoment(d, readSendTime()).error) || addDays(todayIso(), 1));
  const m = sendMoment(day, time);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(10,8,25,0.42)", zIndex: 60 }} />
      <div role="dialog" aria-label="Send to GHL" style={{ position: "fixed", zIndex: 61, left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: "min(400px, calc(100vw - 32px))",
        background: B.surface, borderRadius: 16, boxShadow: "var(--shadow-menu)", border: `1px solid ${B.border2}`, padding: 18, display: "flex", flexDirection: "column", gap: 12 }}>
        <b style={{ fontSize: 16, color: B.text }}>{again ? "Update your GHL draft" : `Send to GHL for ${platformLabel}`}</b>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11.5, color: B.label, fontWeight: 700, flex: "1 1 150px" }}>
            DAY
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} style={{ ...input, fontWeight: 400 }} />
          </label>
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11.5, color: B.label, fontWeight: 700, flex: "1 1 110px" }}>
            TIME
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} style={{ ...input, fontWeight: 400 }} />
          </label>
        </div>
        {accounts.length > 1 && (
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11.5, color: B.label, fontWeight: 700 }}>
            POST AS
            <select value={postAs} onChange={(e) => onPostAs(e.target.value)} style={{ ...input, fontWeight: 400 }}>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}{a.type ? ` (${a.type})` : ""}</option>)}
            </select>
          </label>
        )}
        <div style={{ fontSize: 12, color: m.error ? B.amber : B.dim, lineHeight: 1.5 }}>
          {m.error || "It goes as a draft on that day — nothing posts until you schedule it in GHL. Sending marks this post Done."}
        </div>
        <button type="button" disabled={!!m.error} onClick={() => onSend({ day, time, at: m.iso })}
          style={{ border: "none", borderRadius: 12, background: m.error ? B.surface2 : "var(--accent)", color: m.error ? B.faint : "var(--btn-text)", fontFamily: "inherit", fontWeight: 700, fontSize: 14.5, padding: 13, cursor: m.error ? "default" : "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          <Send size={14} /> {again ? "Update the draft" : "Send as a draft"}
        </button>
        <button type="button" onClick={onClose} style={{ ...btnGhost, padding: "11px 14px", fontSize: 14 }}>Cancel</button>
      </div>
    </>
  );
}

/* "Cross posted:" and the other versions' ICONS, under the name, both ways.
   Her read, 2026-10-06: "Also: in" in a chip "is too prominent, it looks
   like it's the actual platform." So it is quiet: a small grey label, bare
   icons, no names and no chip — the platform she is ON is the dropdown. Each
   icon is still a button named for its platform and post. */
function AlsoLine({ family, onOpen }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, flexWrap: "wrap", fontSize: 11.5, color: B.faint, flexBasis: "100%" }}>
      Cross posted:
      {family.map((p) => {
        const name = `Open the ${platformOf(p.platform).label} post${p.title ? ` — ${p.title}` : ""}`;
        return (
          <button key={p.id} type="button" onClick={() => onOpen?.(p)} title={name} aria-label={name}
            style={{ border: "none", background: "none", padding: 3, cursor: "pointer", display: "inline-flex", opacity: 0.85 }}>
            <PlatformIcon id={p.platform} size={13} />
          </button>
        );
      })}
    </span>
  );
}

/* ── The chat under the post (her pick from the mockup, 2026-10-06) ─────────
   THE BRILL LINE: one teal line under the post, where she types to Brill the
   way she would to Claude. Teal and sparkled, so it never reads as the
   picture's "what's in it" field above it — the one place someone would type
   "make it shorter" by mistake. Enter sends; Shift+Enter is a new line. */
function BrillLine({ inputRef, value, onChange, onSend, busy, placeholder, autoFocus = false }) {
  const can = !busy && !!String(value || "").trim();
  return (
    <div style={{ marginTop: 10, display: "flex", alignItems: "flex-end", gap: 8, border: `1.5px solid ${B.tealLine}`, borderRadius: 22, background: B.surface, padding: "5px 5px 5px 14px" }}>
      <Sparkles size={15} color={B.teal} style={{ flexShrink: 0, marginBottom: 10 }} aria-hidden="true" />
      <textarea ref={inputRef} value={value} rows={1} aria-label="Tell Brill" autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (can) onSend(); } }}
        placeholder={busy ? "Brill is working on it…" : placeholder}
        style={{ flex: 1, minWidth: 0, border: "none", outline: "none", resize: "none", background: "transparent", color: B.text, fontFamily: "inherit", fontSize: 14, lineHeight: 1.5, padding: "8px 0", maxHeight: 120 }} />
      <button type="button" onClick={onSend} disabled={!can} aria-label="Send to Brill" title="Send"
        style={{ width: 36, height: 36, borderRadius: "50%", border: "none", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", cursor: can ? "pointer" : "default",
          background: can ? "var(--accent)" : B.surface2, color: can ? "var(--btn-text)" : B.faint }}>
        <ArrowUp size={16} />
      </button>
    </div>
  );
}

/* BRILL'S LAST WORD, above the line: what it changed, in its own sentence,
   with Undo while the post is still what it left, and the way into the whole
   conversation. One reply, never a transcript — the post is the thing. */
function ReplyStrip({ turn, canUndo, onUndo, count, onOpen }) {
  const note = turn.role === "note";
  return (
    <div style={{ marginTop: 10, border: `1px solid ${note ? B.border : B.tealLine}`, background: note ? B.surface2 : B.tealQuiet, borderRadius: 12, padding: "8px 12px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <span style={{ flex: "1 1 220px", minWidth: 0, fontSize: 13, lineHeight: 1.45, color: B.text }}>
        {!note && <Sparkles size={12} color={B.teal} style={{ marginRight: 6, verticalAlign: "-1px" }} aria-hidden="true" />}
        {turn.content}
      </span>
      {canUndo && (
        <button type="button" onClick={onUndo} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, color: B.teal, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <Undo2 size={13} /> Undo
        </button>
      )}
      {(
        <button type="button" onClick={onOpen} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: B.dim }}>
          Open chat{count ? ` (${count})` : ""}
        </button>
      )}
    </div>
  );
}

/* THE CHAT, FULL PAGE, kept on the post. Her read, 2026-10-06: "See the
   conversation is kind of useless since you can't chat in it. What if it
   just opened up a full page of chat and you could just chat through it?"
   So "Open chat" opens the whole conversation with the Brill line at the
   bottom — the same line, the same send, the same kept turns. Changes still
   land in her post; with the post out of sight, each one says so as a
   version she can open to read, or go back to. Closing it is the way back
   to the post. */
function ChatScreen({ turns, caption, sending, onBack, onClose, line, card }) {
  const versions = versionsOf(turns);
  const byIndex = new Map(versions.map((v) => [v.index, v]));
  const start = turns.find((t) => typeof t.before === "string");
  const [shown, setShown] = useState(null);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: "end" }); }, [turns.length, sending, card]);
  useEffect(() => {
    const k = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const link = { background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 11.5, fontWeight: 700, color: B.teal };
  return (
    <div role="dialog" aria-label="Chat with Brill" style={{ position: "fixed", inset: 0, zIndex: 61, background: B.bg, display: "flex", flexDirection: "column" }}>
      <div style={{ borderBottom: `1px solid ${B.border}`, background: B.surface }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", alignItems: "center", gap: 10, padding: "10px 16px" }}>
          <button type="button" onClick={onClose} style={{ ...btnGhost, padding: "8px 12px", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <ArrowLeft size={15} /> Back to the post
          </button>
          <b style={{ marginLeft: "auto", fontSize: 13.5, color: B.dim, display: "inline-flex", alignItems: "center", gap: 6 }}><Sparkles size={14} color={B.teal} /> Chat with Brill</b>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "16px 16px 8px", display: "flex", flexDirection: "column", gap: 10 }}>
          {start && start.before.trim() && (
            <div style={{ alignSelf: "center", fontSize: 11.5, color: B.faint, display: "flex", gap: 10, alignItems: "center" }}>
              Where you started
              {caption !== start.before && <button type="button" onClick={() => onBack({ n: 1, after: start.before })} style={link}>Go back to this</button>}
            </div>
          )}
          {turns.map((t, i) => {
            if (t.role === "note") return <div key={i} style={{ alignSelf: "center", fontSize: 11.5, color: B.faint }}>{t.content}</div>;
            const mine = t.role === "user";
            const v = byIndex.get(i);
            return (
              <React.Fragment key={i}>
                <div style={{ alignSelf: mine ? "flex-end" : "flex-start", maxWidth: "84%", padding: "10px 13px", borderRadius: 14, fontSize: 14, lineHeight: 1.55, whiteSpace: "pre-wrap",
                  background: mine ? "var(--accent)" : B.surface, color: mine ? "var(--btn-text)" : B.text, border: mine ? "none" : `1px solid ${B.border}` }}>
                  {t.content}
                </div>
                {v && (
                  <div style={{ alignSelf: "flex-start", maxWidth: "84%", paddingLeft: 4, marginTop: -4, display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ fontSize: 11.5, color: B.faint, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                      {caption === v.after ? <span style={{ color: B.teal, fontWeight: 700 }}>Version {v.n} · in your post now</span> : <span>Version {v.n}</span>}
                      <button type="button" onClick={() => setShown(shown === i ? null : i)} style={link}>{shown === i ? "Hide it" : "Read it"}</button>
                      {caption !== v.after && <button type="button" onClick={() => onBack(v)} style={link}>Go back to this</button>}
                    </div>
                    {shown === i && (
                      <div style={{ border: `1px solid ${B.border}`, borderRadius: 12, background: B.surface, padding: "10px 12px", fontSize: 13, lineHeight: 1.6, whiteSpace: "pre-wrap", color: B.text }}>
                        {wordDiff(t.before || "", v.after).filter((p) => p.type !== "cut").map((p, k) => (p.type === "add"
                          ? <mark key={k} style={{ background: B.tealQuiet, color: "inherit", borderRadius: 3 }}>{p.text}</mark>
                          : <span key={k}>{p.text}</span>))}
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {sending && <div style={{ alignSelf: "flex-start", fontSize: 13, color: B.dim, display: "inline-flex", alignItems: "center", gap: 6 }}><Sparkles size={13} color={B.teal} /> Brill is writing…</div>}
          {!turns.length && !sending && <div style={{ fontSize: 13.5, color: B.dim, alignSelf: "center", padding: "24px 0" }}>Tell Brill what to change — or ask what it thinks.</div>}
          {card}
          <div ref={endRef} />
        </div>
      </div>
      <div style={{ borderTop: `1px solid ${B.border}`, background: B.surface }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "4px 16px 14px" }}>{line}</div>
      </div>
    </div>
  );
}

function SuggestionCard({ card, current, onUse, onKeep, onChat }) {
  const stale = !card.busy && card.text && current !== card.base;
  const parts = card.text ? wordDiff(card.base, card.text) : [];
  return (
    <div style={{ marginTop: 12, border: `1px solid ${B.tealLine}`, borderRadius: 12, background: B.surface, overflow: "hidden" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", borderBottom: `1px solid ${B.border}`, background: B.tealQuiet }}>
        <Sparkles size={13} color={B.teal} />
        <span style={{ fontSize: 12.5, fontWeight: 700, color: B.teal }}>{card.label}</span>
      </div>
      {card.busy ? (
        <div style={{ padding: "14px", fontSize: 13, color: B.dim }}>Working on it…</div>
      ) : (
        <div style={{ padding: "12px 14px" }}>
          {card.note && <p style={{ margin: "0 0 10px", fontSize: 13, color: B.mid, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>{card.note}</p>}
          {card.text && (
            <div style={{ fontSize: 13.5, lineHeight: 1.6, color: B.text, whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 360, overflowY: "auto" }}>
              {parts.map((pt, i) => pt.type === "same" ? <span key={i}>{pt.text}</span>
                : pt.type === "cut" ? <span key={i} style={{ textDecoration: "line-through", color: B.faint }}>{pt.text}</span>
                : <span key={i} style={{ background: B.tealQuiet, color: B.text, borderRadius: 3 }}>{pt.text}</span>)}
            </div>
          )}
          {stale && (
            <div style={{ fontSize: 12, color: B.amber, marginTop: 10, lineHeight: 1.5 }}>
              You've changed the post since Brill read it. Use this replaces those changes too.
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
            {card.text && <button type="button" onClick={onUse} style={{ ...quickBtn, fontWeight: 700 }}>Use this</button>}
            <button type="button" onClick={onKeep} style={{ ...btnGhost, padding: "6px 12px", fontSize: 12 }}>{card.text ? "Keep mine" : "Dismiss"}</button>
            {card.ask && !card.text && <button type="button" onClick={onChat} style={{ ...btnGhost, padding: "6px 12px", fontSize: 12 }}>Keep chatting</button>}
          </div>
        </div>
      )}
    </div>
  );
}


/* `icons` on a phone: the drafting screen's bubble-and-eye (PaneToggle), so the
   switch fits on one row beside the platform. The label stays as the name. */
function Seg({ value, onChange, options, icons = false }) {
  return (
    <div role="tablist" style={{ display: "inline-flex", gap: 2, padding: 3, borderRadius: 22, background: B.surface2, border: `1px solid ${B.border}`, flexShrink: 0 }}>
      {options.map(({ id, label, icon: Icon }) => {
        const on = value === id;
        return (
          <button key={id} role="tab" aria-selected={on} onClick={() => onChange(id)} aria-label={label} title={icons ? label : undefined}
            style={{ padding: icons ? "6px 10px" : "7px 16px", borderRadius: 20, fontSize: 13, fontWeight: on ? 700 : 600, cursor: "pointer", fontFamily: "inherit", border: "none", display: "inline-flex", alignItems: "center", gap: 6,
              background: on ? B.surface : "transparent", color: on ? B.text : B.dim, boxShadow: on ? "0 1px 3px rgba(0,0,0,0.10)" : "none" }}>
            <Icon size={icons ? 14 : 13} />{icons ? null : ` ${label}`}
          </button>
        );
      })}
    </div>
  );
}


/* ── The preview: the post as the platform shows it ──────────────────────────
   READ-ONLY (spec decision 7) and it only ever shows what is in the box
   (decision 6) — a preview that can show something other than the post cannot
   be trusted. Her name and photo come from Settings. The fold is where the
   platform cuts it off, which is why this earns its place over a word count:
   the hook has to land above that line. */
export function PostPreview({ post, platform, name, photoUrl }) {
  const [more, setMore] = useState(false);
  useEffect(() => { setMore(false); }, [platform.id]);
  const { shown, folded } = previewFold(post?.caption, platform.id);
  const text = more || !folded ? String(post?.caption || "") : shown;
  const initial = String(name || "Y").trim().charAt(0).toUpperCase();
  const photo = pictureOf(post?.visual);
  const pic = photo ? (
    <img src={photo.url} alt={photo.alt || ""} style={{ width: "100%", display: "block", maxHeight: 360, objectFit: "cover" }} />
  ) : platform.pictureFirst ? (
    <div style={{ aspectRatio: "1 / 1", maxHeight: 280, background: B.surface2, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, color: B.dim, textAlign: "center", padding: 20 }}>
      Instagram posts need a picture. Add one with the picture button under your post.
    </div>
  ) : null;

  const head = (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px" }}>
      {photoUrl
        ? <img src={photoUrl} alt="" style={{ width: 38, height: 38, borderRadius: "50%", objectFit: "cover" }} />
        : <span style={{ width: 38, height: 38, borderRadius: "50%", background: B.tealQuiet, color: B.teal, display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>{initial}</span>}
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: B.text }}>{name}</div>
        <div style={{ fontSize: 11.5, color: B.dim }}>{platform.label} · now</div>
      </div>
    </div>
  );
  const body = (
    <div style={{ padding: "0 14px 12px", fontSize: 13.5, lineHeight: 1.55, color: B.text, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
      {text || <span style={{ color: B.ghost }}>Your post shows here as you write it.</span>}
      {folded && !more && <>{"… "}<button onClick={() => setMore(true)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: B.dim, fontFamily: "inherit", fontSize: 13.5 }}>see more</button></>}
    </div>
  );
  return (
    <div>
      <div style={{ border: `1px solid ${B.border}`, borderRadius: 12, background: B.surface, overflow: "hidden" }}>
        {platform.pictureFirst ? <>{head}{pic}<div style={{ height: 10 }} />{body}</> : <>{head}{body}{pic}</>}
        {String(post?.firstComment || "").trim() && (
          <div style={{ borderTop: `1px solid ${B.border}`, padding: "10px 14px", display: "flex", gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: B.text, flexShrink: 0 }}>{name}</span>
            <span style={{ fontSize: 12.5, color: B.mid, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{post.firstComment}</span>
          </div>
        )}
      </div>
      <p style={{ fontSize: 11.5, color: B.dim, margin: "8px 2px 0", lineHeight: 1.5 }}>
        "See more" is roughly where {platform.label} cuts the post off. Everything above it is your hook.
      </p>
    </div>
  );
}


/* ── The quote card brief, carried over from the old screen ────────────────── */
const isHex = (s) => typeof s === "string" && /^#?[0-9a-fA-F]{6}$/.test(s.trim());
const normHex = (s) => "#" + s.trim().replace(/^#/, "");
const brightness = (hex) => {
  const h = hex.replace("#", "");
  return 0.299 * parseInt(h.slice(0, 2), 16) + 0.587 * parseInt(h.slice(2, 4), 16) + 0.114 * parseInt(h.slice(4, 6), 16);
};
function readableText(bgHex, preferred) {
  const bgLight = brightness(bgHex) > 140;
  if (isHex(preferred) && (brightness(normHex(preferred)) > 140) !== bgLight) return normHex(preferred);
  return bgLight ? "#1a1a1a" : "#FFFFFF";
}

function VisualCard({ visual }) {
  const [copied, setCopied] = useState(false);
  if (visual.type !== "quote" && visual.type !== "lifestyle") return null;
  const copyText = (t) => { navigator.clipboard?.writeText(t); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const shellStyle = { marginTop: 14, background: B.surface, border: `1px solid ${B.border}`, borderRadius: 12, overflow: "hidden" };
  const head = (label, text, copyLabel) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", borderBottom: `1px solid ${B.border}` }}>
      <span style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: B.label, fontWeight: 700 }}>{label}</span>
      <button onClick={() => copyText(text)} style={{ ...btnGhost, padding: "6px 12px", fontSize: 12, color: copied ? B.teal : btnGhost.color }}>{copied ? "Copied" : copyLabel}</button>
    </div>
  );
  if (visual.type === "quote") {
    /* A literal hex, deliberately (as on the old screen): brightness() parses
       it, and the card is an artifact she exports, so it holds still across
       panel modes. */
    const bg = isHex(visual.background_hex) ? normHex(visual.background_hex) : BRAND.tealDark;
    const txt = readableText(bg, visual.text_hex);
    return (
      <div style={shellStyle}>
        {head("Quote card brief", `"${visual.pull_quote}"\n\nTemplate: ${visual.template || "—"}\nBackground: ${bg}\nText: ${txt}\nMood: ${visual.mood || "—"}\nLayout: ${visual.layout_note || "—"}`, "Copy all")}
        <div style={{ margin: 14, borderRadius: 9, background: bg, padding: "26px 20px", textAlign: "center" }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, color: txt, lineHeight: 1.5 }}>"{visual.pull_quote}"</p>
        </div>
        <div style={{ padding: "0 14px 14px", fontSize: 12, color: B.mid, lineHeight: 1.6 }}>
          {visual.template && <div><b style={nb}>Template:</b> {visual.template}</div>}
          {visual.mood && <div><b style={nb}>Mood:</b> {visual.mood}</div>}
          {visual.layout_note && <div><b style={nb}>Layout:</b> {visual.layout_note}</div>}
        </div>
      </div>
    );
  }
  return (
    <div style={shellStyle}>
      {head("Image prompt", visual.image_prompt || "", "Copy prompt")}
      <div style={{ padding: 14, fontSize: 13, color: B.text, lineHeight: 1.6 }}>{visual.image_prompt}</div>
    </div>
  );
}
